import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  CallAPIGetPromise,
  CallAPIPostPromise,
  CallAPIPutPromise,
} from "Common/commaon";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  DollarSign,
  Download,
  Eye,
  FileText,
  FileSignature,
  HardHat,
  Lock,
  MapPin,
  MoreHorizontal,
  Pencil,
  PlusCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  Timer,
  Trash2,
  UserPlus,
  UsersRound,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import api from "Common/api";
import { getAuthHeader } from "helpers/jwt-token-access/auth-token-header";
import Dropzone from "react-dropzone";
import { useDispatch } from "react-redux";
import { sendDocToS3 } from "slices/thunk";
import apiUrl from "slices/apiEndpoint";
import ProcessingModal from "../AddContract/streamModal";
import RichEditorTipTab from "ractTable";
import { ContractLoadingModal } from "components/contractLoadingModal";

type CreatorRole = "contractor" | "subcontractor";

type Milestone = {
  title: string;
  description: string;
  percentage: string;
  amount: string;
};

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

type AgreementForm = {
  creatorRole: CreatorRole;
  title: string;
  contractorUserId: string;
  contractorTitlePrefix: string;
  contractorCompany: string;
  contractorName: string;
  contractorEmail: string;
  contractorPhone: string;
  contractorAddress: string;
  contractorNotes: string;
  subcontractorUserId: string;
  subcontractorTitlePrefix: string;
  subcontractorCompany: string;
  subcontractorName: string;
  subcontractorEmail: string;
  subcontractorPhone: string;
  subcontractorAddress: string;
  ownerUserId: string;
  ownerTitlePrefix: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  projectName: string;
  projectSite: string;
  projectZip: string;
  projectCity: string;
  projectState: string;
  projectCountry: string;
  primeContractDate: string;
  scopeOfWork: string;
  responsibilities: string[];
  legacyResponsibilities: string[];
  descriptionOfOtherResponsibilities: string;
  exclusions: string;
  commencementDate: string;
  completionDate: string;
  completionDateOption: string;
  completionDateSpecific: string;
  completionDateOther: string;
  substantialCompletionDateOption: string;
  substantialCompletionDateSpecific: string;
  substantialCompletionDateOther: string;
  contractSum: string;
  paymentTerms: string;
  paymentAmountOption: string;
  paymentAmountHourlyRate: string;
  paymentAmountOtherDescription: string;
  paymentMethodOption: string;
  paymentMethodOther: string;
  paymentFrequencyOption: string;
  paymentFrequencyOther: string;
  rightToSubcontracting: string;
  assignmentRight: string;
  insuranceCommercialGeneralLiability: boolean | null;
  insuranceAutomobileLiability: boolean;
  insuranceWorkersCompensation: boolean;
  workersCompensationInsuranceLimit: string;
  insuranceUmbrellaExcess: boolean;
  insuranceTradeSpecific: boolean;
  insuranceWorkersCompExempt: boolean;
  insuranceSelfInsured: boolean;
  insuranceCompanyName: string;
  subcontractorInsurance: string;
  subcontractorInsuranceProviderName: string;
  subcontractorWorkersCompensationInsurance: string;
  subcontractorWorkersCompensationInsuranceLimit: string;
  insuranceRequirements: string;
  licenseInfo: string;
  w9DocumentUrl: string;
  licenseDocumentUrl: string;
  contractorAuthorizedRepresentative: string;
  subcontractorAuthorizedRepresentative: string;
  additionalProvisions: string;
};

const subcontractorResponsibilityOptions = [
  {
    id: "labor_supervision_coordination",
    label:
      "labor, supervision, project coordination, layout, field measurements, and daily site management for Subcontractor's work;",
  },
  {
    id: "materials_equipment_tools",
    label:
      "materials, equipment, tools, staging, lifts, ladders, scaffolding, protection, safety equipment, and consumables necessary to complete the work;",
  },
  {
    id: "delivery_storage_protection",
    label:
      "delivery, unloading, storage, protection, and security of Subcontractor's materials and equipment;",
  },
  {
    id: "compliance_codes_safety",
    label:
      "compliance with OSHA, building codes, fire codes, safety rules, site rules, licensing requirements, and permit conditions;",
  },
  {
    id: "cleanup_debris_protection",
    label:
      "daily cleanup, debris removal generated by Subcontractor, and protection of completed and adjacent work;",
  },
  {
    id: "coordination_other_trades",
    label:
      "coordination with Contractor and other trades to avoid delay, interference, damage, or rework;",
  },
  {
    id: "correction_defective_work",
    label:
      "correction of defective, incomplete, damaged, or nonconforming work; and",
  },
  {
    id: "closeout_documents",
    label:
      "submission of closeout documents, warranties, lien waivers, insurance certificates, manuals, as-built information, and other required documentation.",
  },
];

const defaultResponsibilityIds = subcontractorResponsibilityOptions.map(
  (item) => item.id,
);

type ContractPartyPrefill = {
  userId?: number | string | null;
  companyId?: number | string | null;
  company: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  licenseInfo: string;
  w9DocumentUrl: string;
  licenseDocumentUrl: string;
  titlePrefix: string;
  registered?: boolean;
};

const emptyForm: AgreementForm = {
  creatorRole: "subcontractor",
  title: "",
  contractorUserId: "",
  contractorTitlePrefix: "",
  contractorCompany: "",
  contractorName: "",
  contractorEmail: "",
  contractorPhone: "",
  contractorAddress: "",
  contractorNotes: "",
  subcontractorUserId: "",
  subcontractorTitlePrefix: "",
  subcontractorCompany: "",
  subcontractorName: "",
  subcontractorEmail: "",
  subcontractorPhone: "",
  subcontractorAddress: "",
  ownerUserId: "",
  ownerTitlePrefix: "",
  ownerName: "",
  ownerEmail: "",
  ownerPhone: "",
  projectName: "",
  projectSite: "",
  projectZip: "",
  projectCity: "",
  projectState: "",
  projectCountry: "",
  primeContractDate: todayIsoDate(),
  scopeOfWork: "",
  responsibilities: [],
  legacyResponsibilities: [],
  descriptionOfOtherResponsibilities: "",
  exclusions: "",
  commencementDate: "",
  completionDate: "",
  completionDateOption: "specificDate",
  completionDateSpecific: "",
  completionDateOther: "",
  substantialCompletionDateOption: "specificDate",
  substantialCompletionDateSpecific: "",
  substantialCompletionDateOther: "",
  contractSum: "",
  paymentTerms: "",
  paymentAmountOption: "totalAmount",
  paymentAmountHourlyRate: "",
  paymentAmountOtherDescription: "",
  paymentMethodOption: "regular",
  paymentMethodOther: "",
  paymentFrequencyOption: "monthly",
  paymentFrequencyOther: "",
  rightToSubcontracting: "No Right to Subcontracting",
  assignmentRight: "subcontractorCannotAssign",
  insuranceCommercialGeneralLiability: null,
  insuranceAutomobileLiability: false,
  insuranceWorkersCompensation: false,
  workersCompensationInsuranceLimit: "",
  insuranceUmbrellaExcess: false,
  insuranceTradeSpecific: false,
  insuranceWorkersCompExempt: false,
  insuranceSelfInsured: false,
  insuranceCompanyName: "",
  subcontractorInsurance: "yes",
  subcontractorInsuranceProviderName: "",
  subcontractorWorkersCompensationInsurance: "noEmployees",
  subcontractorWorkersCompensationInsuranceLimit: "",
  insuranceRequirements: "",
  licenseInfo: "",
  w9DocumentUrl: "",
  licenseDocumentUrl: "",
  contractorAuthorizedRepresentative: "",
  subcontractorAuthorizedRepresentative: "",
  additionalProvisions: "",
};

const getBlankContractorCreatorForm = (): AgreementForm => ({
  ...emptyForm,
  creatorRole: "contractor",
  primeContractDate: todayIsoDate(),
  completionDateOption: "",
  substantialCompletionDateOption: "",
  paymentAmountOption: "",
  paymentMethodOption: "",
  paymentFrequencyOption: "",
  rightToSubcontracting: "",
  assignmentRight: "",
  subcontractorInsurance: "",
  subcontractorWorkersCompensationInsurance: "",
});

const emptyPrefill: ContractPartyPrefill = {
  userId: null,
  companyId: null,
  company: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  licenseInfo: "",
  w9DocumentUrl: "",
  licenseDocumentUrl: "",
  titlePrefix: "",
  registered: false,
};

function unwrapApiData(response: any) {
  return response?.data?.data || response?.data || response || {};
}

function hasPartyPrefill(prefill: ContractPartyPrefill) {
  return Boolean(
    prefill.company ||
      prefill.name ||
      prefill.email ||
      prefill.phone ||
      prefill.address ||
      prefill.licenseInfo,
  );
}

function partyMatchesPrefill(
  form: AgreementForm,
  party: "contractor" | "subcontractor",
  prefill: ContractPartyPrefill,
) {
  const fields =
    party === "contractor"
      ? [
          form.contractorCompany,
          form.contractorName,
          form.contractorEmail,
          form.contractorPhone,
          form.contractorAddress,
        ]
      : [
          form.subcontractorCompany,
          form.subcontractorName,
          form.subcontractorEmail,
          form.subcontractorPhone,
          form.subcontractorAddress,
        ];
  const source = [
    prefill.company,
    prefill.name,
    prefill.email,
    prefill.phone,
    prefill.address,
  ];
  return (
    fields.some(Boolean) &&
    fields.every((value, index) => !value || value === source[index])
  );
}

function applyPrefillToParty(
  form: AgreementForm,
  party: "contractor" | "subcontractor",
  prefill: ContractPartyPrefill,
  overwriteEmptyTarget = false,
) {
  if (party === "contractor") {
    return {
      ...form,
      contractorUserId:
        overwriteEmptyTarget || !form.contractorUserId
          ? String(prefill.userId || "")
          : form.contractorUserId,
      contractorTitlePrefix:
        overwriteEmptyTarget || !form.contractorTitlePrefix
          ? prefill.titlePrefix
          : form.contractorTitlePrefix,
      contractorCompany:
        overwriteEmptyTarget || !form.contractorCompany
          ? prefill.company
          : form.contractorCompany,
      contractorName:
        overwriteEmptyTarget || !form.contractorName
          ? prefill.name
          : form.contractorName,
      contractorEmail:
        overwriteEmptyTarget || !form.contractorEmail
          ? prefill.email
          : form.contractorEmail,
      contractorPhone:
        overwriteEmptyTarget || !form.contractorPhone
          ? prefill.phone
          : form.contractorPhone,
      contractorAddress:
        overwriteEmptyTarget || !form.contractorAddress
          ? prefill.address
          : form.contractorAddress,
      licenseInfo:
        overwriteEmptyTarget || !form.licenseInfo
          ? prefill.licenseInfo
          : form.licenseInfo,
      w9DocumentUrl:
        overwriteEmptyTarget || !form.w9DocumentUrl
          ? prefill.w9DocumentUrl
          : form.w9DocumentUrl,
      licenseDocumentUrl:
        overwriteEmptyTarget || !form.licenseDocumentUrl
          ? prefill.licenseDocumentUrl
          : form.licenseDocumentUrl,
    };
  }

  return {
    ...form,
    subcontractorUserId:
      overwriteEmptyTarget || !form.subcontractorUserId
        ? String(prefill.userId || "")
        : form.subcontractorUserId,
    subcontractorTitlePrefix:
      overwriteEmptyTarget || !form.subcontractorTitlePrefix
        ? prefill.titlePrefix
        : form.subcontractorTitlePrefix,
    subcontractorCompany:
      overwriteEmptyTarget || !form.subcontractorCompany
        ? prefill.company
        : form.subcontractorCompany,
    subcontractorName:
      overwriteEmptyTarget || !form.subcontractorName
        ? prefill.name
        : form.subcontractorName,
    subcontractorEmail:
      overwriteEmptyTarget || !form.subcontractorEmail
        ? prefill.email
        : form.subcontractorEmail,
    subcontractorPhone:
      overwriteEmptyTarget || !form.subcontractorPhone
        ? prefill.phone
        : form.subcontractorPhone,
    subcontractorAddress:
      overwriteEmptyTarget || !form.subcontractorAddress
        ? prefill.address
        : form.subcontractorAddress,
    licenseInfo:
      overwriteEmptyTarget || !form.licenseInfo
        ? prefill.licenseInfo
        : form.licenseInfo,
    w9DocumentUrl:
      overwriteEmptyTarget || !form.w9DocumentUrl
        ? prefill.w9DocumentUrl
        : form.w9DocumentUrl,
    licenseDocumentUrl:
      overwriteEmptyTarget || !form.licenseDocumentUrl
        ? prefill.licenseDocumentUrl
        : form.licenseDocumentUrl,
  };
}

function clearParty(
  form: AgreementForm,
  party: "contractor" | "subcontractor",
) {
  if (party === "contractor") {
    return {
      ...form,
      contractorCompany: "",
      contractorUserId: "",
      contractorTitlePrefix: "",
      contractorName: "",
      contractorEmail: "",
      contractorPhone: "",
      contractorAddress: "",
    };
  }

  return {
    ...form,
    subcontractorCompany: "",
    subcontractorUserId: "",
    subcontractorTitlePrefix: "",
    subcontractorName: "",
    subcontractorEmail: "",
    subcontractorPhone: "",
    subcontractorAddress: "",
  };
}

const emptyMilestone: Milestone = {
  title: "",
  description: "",
  percentage: "",
  amount: "",
};

function normalizeMoney(value: string) {
  const numeric = Number(String(value || "").replace(/,/g, ""));
  if (!Number.isFinite(numeric)) return "";
  return numeric.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function isValidAmount(value: string, maxValue?: number) {
  const normalized = String(value || "")
    .replace(/,/g, "")
    .trim();
  if (!normalized) return true;
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return false;
  const amount = Number(normalized);
  return (
    Number.isFinite(amount) &&
    amount >= 0 &&
    (maxValue === undefined || amount <= maxValue)
  );
}

function normalizeExtractedAmount(value: unknown) {
  if (value === null || value === undefined || value === "") return "";
  const amount = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(amount) || amount < 0) return "";
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

function phoneDigits(value: string) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
}

function isValidPhone(value: string) {
  return phoneDigits(value).length === 10;
}

function formatPhoneInput(value: string) {
  const rawDigits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  const hasCountryCode = rawDigits.length > 10 && rawDigits.startsWith("1");
  const digits = hasCountryCode ? rawDigits.slice(1) : rawDigits.slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `${hasCountryCode ? "+1 " : ""}(${digits}`;
  if (digits.length < 7)
    return `${hasCountryCode ? "+1 " : ""}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `${hasCountryCode ? "+1 " : ""}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function richTextToPlainText(value: string) {
  if (!value) return "";
  const element = document.createElement("div");
  element.innerHTML = value;
  return (element.innerText || element.textContent || "").trim();
}

function toPayload(
  form: AgreementForm,
  milestones: Milestone[] = [],
  scopeDocumentUrl = "",
) {
  return {
    creatorRole: form.creatorRole,
    title: form.title || form.projectName || "Subcontract Agreement",
    contractor: {
      userId: form.contractorUserId,
      titlePrefix: form.contractorTitlePrefix,
      company: form.contractorCompany,
      name: form.contractorName,
      email: form.contractorEmail,
      phone: form.contractorPhone,
      address: form.contractorAddress,
      notes: form.contractorNotes,
    },
    subcontractor: {
      userId: form.subcontractorUserId,
      titlePrefix: form.subcontractorTitlePrefix,
      company: form.subcontractorCompany,
      name: form.subcontractorName,
      email: form.subcontractorEmail,
      phone: form.subcontractorPhone,
      address: form.subcontractorAddress,
    },
    project: {
      projectName: form.projectName,
      projectSite: form.projectSite,
      projectZip: form.projectZip,
      projectCity: form.projectCity,
      projectState: form.projectState,
      projectCountry: form.projectCountry,
      primeContractDate: form.primeContractDate,
    },
    terms: {
      scopeOfWork: form.scopeOfWork,
      scopeDocumentUrl,
      responsibilities: form.responsibilities,
      legacyResponsibilities: form.legacyResponsibilities,
      descriptionOfOtherResponsibilities:
        form.descriptionOfOtherResponsibilities,
      exclusions: form.exclusions,
      commencementDate: form.commencementDate,
      completionDate: form.completionDate,
      completionDateOption: form.completionDateOption,
      completionDateSpecific:
        form.completionDateSpecific || form.completionDate,
      completionDateOther: form.completionDateOther,
      substantialCompletionDateOption: form.substantialCompletionDateOption,
      substantialCompletionDateSpecific:
        form.substantialCompletionDateSpecific || form.completionDate,
      substantialCompletionDateOther: form.substantialCompletionDateOther,
      contractSum: form.contractSum,
      paymentTerms: "",
      paymentAmountOption: form.paymentAmountOption,
      paymentAmountHourlyRate: form.paymentAmountHourlyRate,
      paymentAmountOtherDescription: form.paymentAmountOtherDescription,
      paymentMethodOption: form.paymentMethodOption,
      paymentMethodOther: form.paymentMethodOther,
      paymentFrequencyOption: form.paymentFrequencyOption,
      paymentFrequencyOther: form.paymentFrequencyOther,
      rightToSubcontracting: form.rightToSubcontracting,
      assignmentRight: form.assignmentRight,
      milestones,
      additionalProvisions: form.additionalProvisions,
    },
    compliance: {
      insuranceOptions: {
        commercialGeneralLiability: form.insuranceCommercialGeneralLiability,
        automobileLiability: form.insuranceAutomobileLiability,
        workersCompensation: form.insuranceWorkersCompensation,
        umbrellaExcess: form.insuranceUmbrellaExcess,
        tradeSpecific: form.insuranceTradeSpecific,
        workersCompExempt: form.insuranceWorkersCompExempt,
        selfInsured: form.insuranceSelfInsured,
        insuranceCompanyName: form.insuranceCompanyName,
        workersCompensationInsuranceLimit:
          form.workersCompensationInsuranceLimit,
        subcontractorInsurance: form.subcontractorInsurance,
        subcontractorInsuranceProviderName:
          form.subcontractorInsuranceProviderName,
        subcontractorWorkersCompensationInsurance:
          form.subcontractorWorkersCompensationInsurance,
        subcontractorWorkersCompensationInsuranceLimit:
          form.subcontractorWorkersCompensationInsuranceLimit,
      },
      insuranceRequirements: form.insuranceRequirements,
      licenseInfo: form.licenseInfo,
      w9DocumentUrl: form.w9DocumentUrl,
      licenseDocumentUrl: form.licenseDocumentUrl,
      contractorAuthorizedRepresentative:
        form.contractorAuthorizedRepresentative,
      subcontractorAuthorizedRepresentative:
        form.subcontractorAuthorizedRepresentative,
    },
  };
}

export const SubcontractAgreementList: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [historyAgreement, setHistoryAgreement] = useState<any>(null);
  const loadRequestId = useRef(0);
  const agreements = items.filter(
    (item) => !item.isChangeOrder && !item.parentAgreementId,
  );
  const changeOrders = items.filter(
    (item) => item.parentAgreementId === historyAgreement?.id,
  );

  const load = () => {
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setLoadError("");
    CallAPIGetPromise("/contract/subcontract-agreements")
      .then((res: any) => {
        if (requestId === loadRequestId.current) {
          setItems(unwrapApiData(res) || []);
        }
      })
      .catch(() => {
        if (requestId === loadRequestId.current) {
          setLoadError("We couldn't load your subcontract agreements.");
        }
      })
      .finally(() => {
        if (requestId === loadRequestId.current) setLoading(false);
      });
  };

  useEffect(() => {
    load();
    return () => {
      loadRequestId.current += 1;
    };
  }, []);

  const statusLabel = (status: string) => {
    switch (status) {
      case "fully_executed":
        return "Fully signed";
      case "contractor_signed":
        return "Subcontractor signature pending";
      case "subcontractor_signed":
        return "Contractor signature pending";
      case "sent":
        return "Sent for signature";
      case "draft":
        return "Draft";
      default:
        return status
          ? status.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase())
          : "Draft";
    }
  };

  const statusClass = (status: string) => {
    if (status === "fully_executed") return "contract-status-success";
    if (status === "draft") return "contract-status-draft";
    return "contract-status-awaiting";
  };

  const filteredAgreements = agreements.filter((item) => {
    const payload = item.payload || {};
    const role = item.viewerRole === "subcontractor" ? "subcontractor" : "contractor";
    const otherParty =
      role === "subcontractor"
        ? payload.contractor?.company || payload.contractor?.name || item.contractorEmail
        : payload.subcontractor?.company || payload.subcontractor?.name || item.subcontractorEmail;
    const searchableText = [
      item.title,
      item.id,
      item.projectName,
      item.hicProjectId,
      otherParty,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const normalizedQuery = query.trim().toLowerCase();

    return (
      (!normalizedQuery || searchableText.includes(normalizedQuery)) &&
      (statusFilter === "all" || item.status === statusFilter) &&
      (roleFilter === "all" || role === roleFilter)
    );
  });

  const resetFilters = () => {
    setQuery("");
    setStatusFilter("all");
    setRoleFilter("all");
  };

  const hasActiveFilters = Boolean(
    query.trim() || statusFilter !== "all" || roleFilter !== "all",
  );

  if (loading) {
    return (
      <Card className="contract-list-card subcontract-list-card">
        <Card.Body className="subcontract-list-loading" role="status" aria-label="Loading subcontract agreements">
          <Spinner animation="border" size="sm" />
          <span>Loading subcontract agreements...</span>
        </Card.Body>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Alert variant="danger" className="subcontract-list-error">
        <div>
          <div className="fw-semibold">Unable to load agreements</div>
          <div>{loadError} Check your connection and try again.</div>
        </div>
        <Button variant="outline-danger" size="sm" onClick={load}>
          <RefreshCw size={14} className="me-1" /> Retry
        </Button>
      </Alert>
    );
  }

  if (!agreements.length) {
    return (
      <Card className="contract-list-card subcontract-list-card">
        <Card.Body className="text-center py-5">
          <FileText className="mb-3 text-primary" size={42} />
          <h5>No subcontract agreements yet</h5>
          <p className="text-muted mb-4">
            Create a subcontractor-to-GC agreement and keep the latest generated
            PDF in one place.
          </p>
          <Button onClick={() => navigate("/create-contract?type=subcontract")}>
            <PlusCircle size={16} className="me-2" />
            Create Subcontract Agreement
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="contract-list-card subcontract-list-card">
      <Card.Body>
        {!compact ? (
          <div className="subcontract-list-header">
            <div>
              <h5 className="mb-1">Subcontract Agreements</h5>
              <p className="text-muted mb-0">
                Track agreements, signatures, PDFs, and change orders.
              </p>
            </div>
            <Button
              onClick={() => navigate("/create-contract?type=subcontract")}
            >
              <PlusCircle size={16} className="me-2" />
              Create Subcontract Agreement
            </Button>
          </div>
        ) : null}
        <div className="subcontract-filter-grid">
          <Form.Group>
            <Form.Label>Search agreements</Form.Label>
            <div className="app-search-field">
              <Search className="app-field-icon" aria-hidden="true" />
              <Form.Control
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Agreement, project, or party"
              />
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent for signature</option>
              <option value="contractor_signed">Subcontractor signature pending</option>
              <option value="subcontractor_signed">Contractor signature pending</option>
              <option value="fully_executed">Fully signed</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>My role</Form.Label>
            <Form.Select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value="all">All roles</option>
              <option value="contractor">Contractor</option>
              <option value="subcontractor">Subcontractor</option>
            </Form.Select>
          </Form.Group>
          <div className="subcontract-filter-reset">
            <Button
              variant="outline-secondary"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </Button>
          </div>
        </div>
        <div className="subcontract-results-summary" aria-live="polite">
          Showing {filteredAgreements.length} of {agreements.length} agreements
        </div>
        <div className="table-responsive subcontract-table-responsive">
          <Table hover className="align-middle mb-0 subcontract-list-table">
            <thead>
              <tr>
                <th>Agreement</th>
                <th>Other Party</th>
                <th>Project</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="text-end">Next Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgreements.map((item) => {
                const payload = item.payload || {};
                const myRole =
                  item.viewerRole === "subcontractor"
                    ? "Subcontractor"
                    : "Contractor";
                const otherParty =
                  item.viewerRole === "subcontractor"
                    ? payload.contractor?.company ||
                      payload.contractor?.name ||
                      item.contractorEmail
                    : payload.subcontractor?.company ||
                      payload.subcontractor?.name ||
                      item.subcontractorEmail;
                const changeOrderCount = items.filter(
                  (agreement) => agreement.parentAgreementId === item.id,
                ).length;
                return (
                  <tr key={item.id}>
                    <td className="subcontract-cell-agreement">
                      <div className="subcontract-agreement-title">
                        {item.title || "Subcontract Agreement"}
                      </div>
                      <div className="subcontract-row-meta">
                        <span>#{item.id}</span>
                        <span aria-hidden="true">·</span>
                        <span>{myRole}</span>
                      </div>
                    </td>
                    <td className="subcontract-cell-party">{otherParty || "Not selected"}</td>
                    <td className="subcontract-cell-project">
                      <div className="subcontract-project-name">
                        {item.projectName || "Project reference pending"}
                      </div>
                      {item.hicProjectId ? (
                        <span className="subcontract-hic-link">
                          Connected to HIC #{item.hicProjectId}
                        </span>
                      ) : null}
                    </td>
                    <td className="subcontract-cell-amount">
                      {item.contractSum
                        ? normalizeMoney(String(item.contractSum))
                        : "—"}
                    </td>
                    <td className="subcontract-cell-status">
                      <span className={`contract-status-badge ${statusClass(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td className="subcontract-cell-actions">
                      <div className="subcontract-row-actions">
                        <Button
                          size="sm"
                          variant={item.canSign ? "primary" : "outline-primary"}
                          onClick={() =>
                            navigate(
                              `/create-contract?type=subcontract&id=${item.id}${item.hicProjectId ? `&hicProjectId=${item.hicProjectId}` : ""}`,
                            )
                          }
                        >
                          {item.canSign ? (
                            <FileSignature size={14} className="me-1" />
                          ) : item.canEdit ? (
                            <Pencil size={14} className="me-1" />
                          ) : (
                            <Eye size={14} className="me-1" />
                          )}
                          {item.canSign ? "Review & Sign" : item.canEdit ? "Edit" : "Review"}
                        </Button>
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            size="sm"
                            variant="outline-secondary"
                            className="contract-more-toggle"
                            aria-label={`More actions for ${item.title || "subcontract agreement"}`}
                          >
                            <MoreHorizontal size={17} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="contract-actions-menu">
                            <Dropdown.Item
                              disabled={!item.contractUrl}
                              onClick={() => item.contractUrl && window.open(item.contractUrl, "_blank", "noopener,noreferrer")}
                            >
                              <Download size={15} />
                              {item.contractUrl ? "View PDF" : "PDF not generated"}
                            </Dropdown.Item>
                            {item.status === "fully_executed" && !item.isChangeOrder ? (
                              <Dropdown.Item
                                onClick={() =>
                                  navigate(`/create-contract?type=subcontract&changeOrderFor=${item.id}`)
                                }
                              >
                                <PlusCircle size={15} /> Create change order
                              </Dropdown.Item>
                            ) : null}
                            {changeOrderCount > 0 ? (
                              <Dropdown.Item onClick={() => setHistoryAgreement(item)}>
                                <ClipboardCheck size={15} />
                                Change order history ({changeOrderCount})
                              </Dropdown.Item>
                            ) : null}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredAgreements.length ? (
                <tr>
                  <td colSpan={6} className="subcontract-filter-empty">
                    <Search size={24} aria-hidden="true" />
                    <div className="fw-semibold">No matching agreements</div>
                    <div className="text-muted">Try a different search or clear the filters.</div>
                    <Button size="sm" variant="outline-primary" onClick={resetFilters}>
                      Clear filters
                    </Button>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </Card.Body>
      <Modal
        show={Boolean(historyAgreement)}
        onHide={() => setHistoryAgreement(null)}
        centered
        size="lg"
        dialogClassName="subcontract-history-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Order History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            {historyAgreement?.title || "Subcontract Agreement"} #
            {historyAgreement?.id}
          </p>
          {changeOrders.length ? (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Change Order</th>
                    <th>Extra Work</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Document</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {changeOrders.map((changeOrder) => (
                    <tr key={changeOrder.id}>
                      <td>
                        <div className="fw-semibold">
                          {changeOrder.title || "Change Order"}
                        </div>
                        <small className="text-muted">#{changeOrder.id}</small>
                      </td>
                      <td>
                        {richTextToPlainText(
                          changeOrder.payload?.terms?.scopeOfWork || "",
                        ) || "Scope pending"}
                      </td>
                      <td>
                        {changeOrder.contractSum
                          ? normalizeMoney(String(changeOrder.contractSum))
                          : "—"}
                      </td>
                      <td>
                        <Badge
                          bg={
                            changeOrder.status === "fully_executed"
                              ? "success"
                              : changeOrder.status === "sent"
                                ? "warning"
                                : "secondary"
                          }
                        >
                          {changeOrder.status}
                        </Badge>
                      </td>
                      <td>
                        {changeOrder.contractUrl ? (
                          <a
                            href={changeOrder.contractUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <Download size={14} className="me-1" />
                            PDF
                          </a>
                        ) : (
                          <span className="text-muted">Not generated</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() =>
                            navigate(
                              `/create-contract?type=subcontract&id=${changeOrder.id}${changeOrder.hicProjectId ? `&hicProjectId=${changeOrder.hicProjectId}` : ""}`,
                            )
                          }
                        >
                          {changeOrder.canSign ? "Review & Sign" : "Review"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              No change orders have been created for this subcontract agreement.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Card>
  );
};

const wizardSteps = [
  "Parties",
  "Project Info",
  "Contractor / GC",
  "Milestones",
  "Checklist",
];

const StepProgress: React.FC<{
  step: number;
  goToStep: (nextStep: number) => void;
  minimumStep?: number;
  creatorRole?: CreatorRole;
}> = ({ step, goToStep, minimumStep = 1, creatorRole }) => (
  <div className="step-arrow-nav mb-4 overflow-auto pb-2">
    <Nav
      as="ul"
      variant="pills"
      className="custom-nav nav-justified flex-nowrap"
      style={{ minWidth: 720 }}
    >
      {wizardSteps.map((label, index) => {
        const nextStep = index + 1;
        if (nextStep < minimumStep) return null;
        const visibleLabel =
          nextStep === 3
            ? creatorRole === "contractor"
              ? "Subcontractor"
              : "Contractor / GC"
            : label;
        return (
          <Nav.Item as="li" key={visibleLabel}>
            <Nav.Link
              as="button"
              type="button"
              eventKey={String(nextStep)}
              active={step === nextStep}
              className={`step-subcontract-${nextStep}`}
              onClick={() => goToStep(nextStep)}
            >
              {visibleLabel}
            </Nav.Link>
          </Nav.Item>
        );
      })}
    </Nav>
  </div>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <Form.Group>
    <Form.Label className="fw-semibold small">
      {label} {required ? <span className="text-danger">*</span> : null}
    </Form.Label>
    {children}
  </Form.Group>
);

type SigningStatus = "idle" | "submitting" | "finalizing";

const SigningProgressModal: React.FC<{ status: SigningStatus }> = ({
  status,
}) => (
  <Modal
    show={status !== "idle"}
    centered
    backdrop="static"
    keyboard={false}
    dialogClassName="subcontract-signing-modal"
    contentClassName="border-0 shadow-lg subcontract-signing-modal-content"
  >
    <Modal.Body className="tailwind-prefix px-6 py-8 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
        <Spinner animation="border" role="status" className="text-blue-600" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-gray-900">
        {status === "submitting"
          ? "Signing your subcontract agreement"
          : "Finalizing your subcontract agreement"}
      </h3>
      <p className="mb-0 text-sm text-gray-600">
        {status === "submitting"
          ? "Your signature is being securely submitted. Please do not close this window."
          : "Your signature was submitted. We are updating the signed agreement details."}
      </p>
    </Modal.Body>
  </Modal>
);

const SubcontractAgreementPage: React.FC = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAgreementId = searchParams.get("id");
  const changeOrderFor = searchParams.get("changeOrderFor");
  // Support the original misspelled deep link as well as the canonical name.
  const hicProjectId =
    searchParams.get("hicProjectId") || searchParams.get("hicProjetId");
  const isNewHicFlow = Boolean(hicProjectId && !editAgreementId);
  const minimumStep = isNewHicFlow ? 2 : 1;
  const [capabilities, setCapabilities] = useState<any>(null);
  const [loadingCapabilities, setLoadingCapabilities] = useState(true);
  const [prefillLoading, setPrefillLoading] = useState(true);
  const [currentUserPrefill, setCurrentUserPrefill] =
    useState<ContractPartyPrefill>(emptyPrefill);
  const [saving, setSaving] = useState<"draft" | "send" | null>(null);
  const [step, setStep] = useState(isNewHicFlow ? 2 : 1);
  const [generatedAgreement, setGeneratedAgreement] = useState<any>(null);
  const [loadedAgreement, setLoadedAgreement] = useState<any>(null);
  const canReturnToHic = Boolean(
    hicProjectId && (!editAgreementId || loadedAgreement?.viewerRole === "contractor"),
  );
  const isChangeOrder = Boolean(
    changeOrderFor || loadedAgreement?.isChangeOrder,
  );
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signingStatus, setSigningStatus] = useState<SigningStatus>("idle");
  const [resendingGuestInvite, setResendingGuestInvite] = useState(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const milestoneWarningRef = useRef<HTMLDivElement | null>(null);
  const previousStepRef = useRef(1);
  const [form, setForm] = useState<AgreementForm>(() =>
    isNewHicFlow ? getBlankContractorCreatorForm() : emptyForm,
  );
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [milestoneEditorOpen, setMilestoneEditorOpen] = useState(true);
  const [editingMilestoneIndex, setEditingMilestoneIndex] = useState<
    number | null
  >(null);
  const [milestoneDraft, setMilestoneDraft] = useState<Milestone>({
    ...emptyMilestone,
  });
  const [partySearchQuery, setPartySearchQuery] = useState("");
  const [partySearchResults, setPartySearchResults] = useState<
    ContractPartyPrefill[]
  >([]);
  const [partySearchLoading, setPartySearchLoading] = useState(false);
  const [partySearchError, setPartySearchError] = useState("");
  const [selectedRegisteredParty, setSelectedRegisteredParty] =
    useState<ContractPartyPrefill | null>(null);
  const [useExternalParty, setUseExternalParty] = useState(false);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");
  const [ownerSearchResults, setOwnerSearchResults] = useState<
    ContractPartyPrefill[]
  >([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [ownerSearchError, setOwnerSearchError] = useState("");
  const [selectedOwner, setSelectedOwner] =
    useState<ContractPartyPrefill | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [showStepValidation, setShowStepValidation] = useState(false);
  const [scopeDocumentUrl, setScopeDocumentUrl] = useState("");
  const [scopeUploading, setScopeUploading] = useState(false);
  const [projectZipLoading, setProjectZipLoading] = useState(false);
  const [projectZipError, setProjectZipError] = useState("");
  const [showScopeExtraction, setShowScopeExtraction] = useState(false);
  const [scopeExtractionStage, setScopeExtractionStage] =
    useState<any>("uploading");
  const [extractedScopePreview, setExtractedScopePreview] = useState("");
  const [aiExtractedContractSum, setAiExtractedContractSum] = useState("");
  const [figuresAcknowledged, setFiguresAcknowledged] = useState(false);
  const [aiAmountVerified, setAiAmountVerified] = useState(false);
  const [hicPrefillLoading, setHicPrefillLoading] = useState(false);
  const [hicPrefillError, setHicPrefillError] = useState("");

  useEffect(() => {
    // Existing agreements already contain the signed HIC snapshot in their
    // payload. Fetching the live homeowner project while reviewing/signing
    // wrongly fails for the subcontractor, who is not expected to have HIC
    // access, and produces a misleading error on an otherwise valid contract.
    if (!isNewHicFlow) {
      setHicPrefillLoading(false);
      setHicPrefillError("");
      return;
    }

    setHicPrefillLoading(true);
    setHicPrefillError("");
    CallAPIGetPromise(
      `/contract/subcontract-agreements/hic/${hicProjectId}/prefill`,
    )
      .then((res: any) => {
        const data = unwrapApiData(res);
        const project = data?.project || {};
        const contractor = data?.contractor || {};
        setForm((current) => {
          const next = {
            ...current,
            ...(isNewHicFlow
              ? { creatorRole: "contractor" as CreatorRole }
              : {}),
            ownerUserId: String(project.ownerUserId || ""),
            ownerName: project.ownerName || "",
            ownerEmail: project.ownerEmail || "",
            ownerPhone: project.ownerPhone || "",
            projectName: project.projectName || current.projectName,
            projectSite: project.projectSite || current.projectSite,
            projectZip: project.projectZip || current.projectZip,
            projectCity: project.projectCity || current.projectCity,
            projectState: project.projectState || current.projectState,
            projectCountry: project.projectCountry || current.projectCountry,
          };
          if (!isNewHicFlow) return next;
          return applyPrefillToParty(
            next,
            "contractor",
            {
              ...emptyPrefill,
              userId: contractor.userId || null,
              companyId: contractor.companyId || null,
              company: contractor.company || "",
              name: contractor.name || "",
              email: contractor.email || "",
              phone: contractor.phone || "",
              address: contractor.address || "",
              licenseInfo: contractor.licenseInfo || "",
              registered: Boolean(contractor.userId),
            },
            true,
          );
        });
        if (project.ownerUserId || project.ownerName || project.ownerEmail) {
          setSelectedOwner({
            ...emptyPrefill,
            userId: project.ownerUserId || null,
            name: project.ownerName || "",
            email: project.ownerEmail || "",
            phone: project.ownerPhone || "",
            registered: Boolean(project.ownerUserId),
          });
        }
      })
      .catch((error: any) => {
        setHicPrefillError(
          error?.response?.data?.message ||
            "Unable to load homeowner project details.",
        );
      })
      .finally(() => setHicPrefillLoading(false));
  }, [hicProjectId, isNewHicFlow]);

  useEffect(() => {
    CallAPIGetPromise("/contract/subcontract-agreements/capabilities")
      .then((res: any) => setCapabilities(res?.data))
      .catch(() =>
        setCapabilities({
          canCreateSubcontractAgreement: false,
          reason: "Unable to verify subscription.",
        }),
      )
      .finally(() => setLoadingCapabilities(false));
  }, []);

  useEffect(() => {
    const stepChanged = previousStepRef.current !== step;
    previousStepRef.current = step;
    window.requestAnimationFrame(() => {
      const wizard = document.querySelector(".subcontract-wizard-card");
      wizard?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (stepChanged && step > 1 && step < 5) {
        window.setTimeout(() => {
          wizard
            ?.querySelector<HTMLElement>(
              "input:not([type='hidden']):not([type='radio']):not([type='checkbox']):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled])",
            )
            ?.focus({ preventScroll: true });
        }, 450);
      }
    });
  }, [step]);

  useEffect(() => {
    CallAPIGetPromise(
      `/contract/subcontract-agreements/current-party-profile?_=${Date.now()}`,
    )
      .then((res: any) => {
        const data = unwrapApiData(res);
        const nextPrefill = {
          ...emptyPrefill,
          ...data,
        };
        setCurrentUserPrefill(nextPrefill);
        if (hasPartyPrefill(nextPrefill)) {
          setForm((current) =>
            applyPrefillToParty(current, current.creatorRole, nextPrefill),
          );
        }
      })
      .catch(() => {
        setCurrentUserPrefill(emptyPrefill);
      })
      .finally(() => setPrefillLoading(false));
  }, []);

  useEffect(() => {
    const agreementIdToLoad = editAgreementId || changeOrderFor;
    if (!agreementIdToLoad) return;

    CallAPIGetPromise(`/contract/subcontract-agreements/${agreementIdToLoad}`)
      .then((res: any) => {
        const agreement = unwrapApiData(res);
        setLoadedAgreement(agreement);
        if (editAgreementId && agreement?.contractUrl) {
          setGeneratedAgreement(agreement);
          setStep(5);
        }
        const payload = agreement?.payload || {};
        const contractor = payload.contractor || {};
        const subcontractor = payload.subcontractor || {};
        const project = payload.project || {};
        const terms = payload.terms || {};
        const compliance = payload.compliance || {};
        const insuranceOptions = compliance.insuranceOptions || {};
        const creatorRole: CreatorRole =
          payload.creatorRole === "subcontractor"
            ? "subcontractor"
            : "contractor";
        const nextForm: AgreementForm = {
          ...emptyForm,
          creatorRole,
          title: agreement.title || payload.title || "",
          contractorUserId: String(
            contractor.userId || agreement.contractorUserId || "",
          ),
          contractorTitlePrefix: contractor.titlePrefix || "",
          contractorCompany: contractor.company || "",
          contractorName: contractor.name || "",
          contractorEmail: contractor.email || agreement.contractorEmail || "",
          contractorPhone: contractor.phone || "",
          contractorAddress: contractor.address || "",
          contractorNotes: contractor.notes || "",
          subcontractorUserId: String(
            subcontractor.userId || agreement.subcontractorUserId || "",
          ),
          subcontractorTitlePrefix: subcontractor.titlePrefix || "",
          subcontractorCompany: subcontractor.company || "",
          subcontractorName: subcontractor.name || "",
          subcontractorEmail:
            subcontractor.email || agreement.subcontractorEmail || "",
          subcontractorPhone: subcontractor.phone || "",
          subcontractorAddress: subcontractor.address || "",
          ownerUserId: String(project.ownerUserId || ""),
          ownerTitlePrefix: project.ownerTitlePrefix || "",
          ownerName: project.ownerName || "",
          ownerEmail: project.ownerEmail || "",
          ownerPhone: project.ownerPhone || "",
          projectName: project.projectName || agreement.projectName || "",
          projectSite: project.projectSite || agreement.projectSite || "",
          projectZip: project.projectZip || project.zip || "",
          projectCity: project.projectCity || project.city || "",
          projectState: project.projectState || project.state || "",
          projectCountry: project.projectCountry || project.country || "",
          primeContractDate: project.primeContractDate || "",
          scopeOfWork: terms.scopeOfWork || "",
          responsibilities: Array.isArray(terms.responsibilities)
            ? terms.responsibilities.filter((id: string) =>
                defaultResponsibilityIds.includes(id),
              )
            : [],
          legacyResponsibilities: Array.isArray(terms.legacyResponsibilities)
            ? terms.legacyResponsibilities
            : [],
          descriptionOfOtherResponsibilities:
            terms.descriptionOfOtherResponsibilities || "",
          exclusions: terms.exclusions || "",
          commencementDate: terms.commencementDate || "",
          completionDate: terms.completionDate || "",
          completionDateOption:
            terms.completionDateOption || emptyForm.completionDateOption,
          completionDateSpecific:
            terms.completionDateSpecific || terms.completionDate || "",
          completionDateOther: terms.completionDateOther || "",
          substantialCompletionDateOption:
            terms.substantialCompletionDateOption ||
            emptyForm.substantialCompletionDateOption,
          substantialCompletionDateSpecific:
            terms.substantialCompletionDateSpecific ||
            terms.completionDate ||
            "",
          substantialCompletionDateOther:
            terms.substantialCompletionDateOther || "",
          contractSum: String(terms.contractSum || agreement.contractSum || ""),
          paymentTerms: terms.paymentTerms || emptyForm.paymentTerms,
          paymentAmountOption:
            terms.paymentAmountOption || emptyForm.paymentAmountOption,
          paymentAmountHourlyRate: terms.paymentAmountHourlyRate || "",
          paymentAmountOtherDescription:
            terms.paymentAmountOtherDescription || "",
          paymentMethodOption:
            terms.paymentMethodOption || emptyForm.paymentMethodOption,
          paymentMethodOther: terms.paymentMethodOther || "",
          paymentFrequencyOption:
            terms.paymentFrequencyOption || emptyForm.paymentFrequencyOption,
          paymentFrequencyOther: terms.paymentFrequencyOther || "",
          rightToSubcontracting:
            terms.rightToSubcontracting || emptyForm.rightToSubcontracting,
          assignmentRight: terms.assignmentRight || emptyForm.assignmentRight,
          insuranceCommercialGeneralLiability:
            insuranceOptions.commercialGeneralLiability ??
            emptyForm.insuranceCommercialGeneralLiability,
          insuranceAutomobileLiability:
            insuranceOptions.automobileLiability ??
            emptyForm.insuranceAutomobileLiability,
          insuranceWorkersCompensation:
            insuranceOptions.workersCompensation ??
            emptyForm.insuranceWorkersCompensation,
          workersCompensationInsuranceLimit:
            insuranceOptions.workersCompensationInsuranceLimit || "",
          insuranceUmbrellaExcess:
            insuranceOptions.umbrellaExcess ??
            emptyForm.insuranceUmbrellaExcess,
          insuranceTradeSpecific:
            insuranceOptions.tradeSpecific ?? emptyForm.insuranceTradeSpecific,
          insuranceWorkersCompExempt:
            insuranceOptions.workersCompExempt ??
            emptyForm.insuranceWorkersCompExempt,
          insuranceSelfInsured:
            insuranceOptions.selfInsured ?? emptyForm.insuranceSelfInsured,
          insuranceCompanyName: insuranceOptions.insuranceCompanyName || "",
          subcontractorInsurance:
            insuranceOptions.subcontractorInsurance ||
            emptyForm.subcontractorInsurance,
          subcontractorInsuranceProviderName:
            insuranceOptions.subcontractorInsuranceProviderName || "",
          subcontractorWorkersCompensationInsurance:
            insuranceOptions.subcontractorWorkersCompensationInsurance ||
            emptyForm.subcontractorWorkersCompensationInsurance,
          subcontractorWorkersCompensationInsuranceLimit:
            insuranceOptions.subcontractorWorkersCompensationInsuranceLimit ||
            "",
          insuranceRequirements:
            compliance.insuranceRequirements || emptyForm.insuranceRequirements,
          licenseInfo: compliance.licenseInfo || "",
          w9DocumentUrl: compliance.w9DocumentUrl || "",
          licenseDocumentUrl: compliance.licenseDocumentUrl || "",
          contractorAuthorizedRepresentative:
            compliance.contractorAuthorizedRepresentative || "",
          subcontractorAuthorizedRepresentative:
            compliance.subcontractorAuthorizedRepresentative || "",
          additionalProvisions: terms.additionalProvisions || "",
        };

        setForm(
          changeOrderFor
            ? {
                ...nextForm,
                title: `Change Order — ${agreement.title || `Agreement #${agreement.id}`}`,
                scopeOfWork: "",
                descriptionOfOtherResponsibilities: "",
                exclusions: "",
                contractSum: "",
                commencementDate: "",
                completionDate: "",
                completionDateSpecific: "",
                completionDateOther: "",
                substantialCompletionDateSpecific: "",
                substantialCompletionDateOther: "",
                additionalProvisions: "",
              }
            : nextForm,
        );
        setMilestones(
          changeOrderFor
            ? []
            : Array.isArray(terms.milestones) && terms.milestones.length
              ? terms.milestones.map((milestone: any) => ({
                  title: milestone.title || "",
                  description: milestone.description || "",
                  percentage: String(milestone.percentage || ""),
                  amount: String(milestone.amount || ""),
                }))
              : [],
        );
        setMilestoneEditorOpen(Boolean(changeOrderFor));
        setScopeDocumentUrl(changeOrderFor ? "" : terms.scopeDocumentUrl || "");

        const otherParty =
          creatorRole === "contractor" ? subcontractor : contractor;
        if (
          otherParty?.email ||
          otherParty?.userId ||
          otherParty?.company ||
          otherParty?.name
        ) {
          const otherUserId =
            otherParty.userId ||
            (creatorRole === "contractor"
              ? agreement.subcontractorUserId
              : agreement.contractorUserId) ||
            null;
          if (otherUserId) {
            setSelectedRegisteredParty({
              ...emptyPrefill,
              userId: otherUserId,
              company: otherParty.company || "",
              name: otherParty.name || "",
              email: otherParty.email || "",
              phone: otherParty.phone || "",
              address: otherParty.address || "",
              licenseInfo: compliance.licenseInfo || "",
              w9DocumentUrl: compliance.w9DocumentUrl || "",
              licenseDocumentUrl: compliance.licenseDocumentUrl || "",
              titlePrefix: otherParty.titlePrefix || "",
              registered: true,
            });
          } else {
            setSelectedRegisteredParty(null);
            setUseExternalParty(true);
          }
        }

        if (project.ownerName || project.ownerEmail || project.ownerUserId) {
          setSelectedOwner({
            ...emptyPrefill,
            userId: project.ownerUserId || null,
            company: "",
            name: project.ownerName || "",
            email: project.ownerEmail || "",
            phone: project.ownerPhone || "",
            address: "",
            titlePrefix: project.ownerTitlePrefix || "",
            registered: true,
          });
        }
      })
      .catch((error: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Unable to load subcontract agreement for editing.",
        );
      });
  }, [editAgreementId, changeOrderFor]);

  const openSignatureModal = (
    agreement = generatedAgreement || loadedAgreement,
  ) => {
    if (!agreement?.canSign) {
      toast.info("This agreement has already been signed by you.");
      return;
    }
    setShowSignatureModal(true);
  };

  const submitAgreementSignature = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.warning("Please draw your signature before submitting.");
      return;
    }

    const agreement = generatedAgreement || loadedAgreement;
    const headers = getAuthHeader();
    if (!agreement?.id || !headers) return;
    const creatorIsSigning = agreement.viewerRole === agreement.creatorRole;
    const hasGuestParty = !form.contractorUserId || !form.subcontractorUserId;

    setSigning(true);
    setSigningStatus("submitting");
    setShowSignatureModal(false);
    try {
      const signatureDataUrl = signatureRef.current.toDataURL("image/png");
      const signatureBlob = await fetch(signatureDataUrl).then((response) =>
        response.blob(),
      );
      const body = new FormData();
      body.append("file", signatureBlob, "signature.png");
      const response: any = await api.post(
        `/contract/subcontract-agreements/${agreement.id}/sign`,
        body,
        {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        },
      );
      const signedAgreement = response?.data;
      setSigningStatus("finalizing");
      setGeneratedAgreement(signedAgreement);
      setLoadedAgreement(signedAgreement);
      signatureRef.current.clear();
      toast.success(
        response?.message || "Subcontract agreement signed successfully.",
      );
      if (
        creatorIsSigning &&
        hasGuestParty &&
        signedAgreement?.status !== "fully_executed"
      ) {
        try {
          await CallAPIPostPromise(
            `/contract/subcontract-agreements/${agreement.id}/generate-pdf`,
            { sendGuestInvite: true },
          );
          toast.success("Secure signing email sent to the guest.");
        } catch (inviteError: any) {
          toast.warning(
            inviteError?.response?.data?.message ||
              "The contract was signed, but the signing email could not be sent. Please use Resend Email.",
          );
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Unable to sign subcontract agreement.",
      );
    } finally {
      setSigningStatus("idle");
      setSigning(false);
    }
  };

  const resendSignatureEmail = async (
    role?: "contractor" | "subcontractor",
  ) => {
    if (!generatedAgreement?.id) return;
    setResendingGuestInvite(true);
    try {
      const response: any = await CallAPIPostPromise(
        `/contract/subcontract-agreements/${generatedAgreement.id}/resend-signature-email`,
        role ? { role } : {},
      );
      setGeneratedAgreement(response?.data || generatedAgreement);
      toast.success(response?.message || "Signing email sent.");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Unable to resend the signing email.",
      );
    } finally {
      setResendingGuestInvite(false);
    }
  };

  const otherDetailsTitle =
    form.creatorRole === "contractor"
      ? "Add Subcontractor Details"
      : "Add Contractor / GC Details";
  const milestoneTotal = milestones.reduce(
    (sum, item) =>
      sum + (Number(String(item.amount || "").replace(/,/g, "")) || 0),
    0,
  );
  const milestonePercentageTotal = milestones.reduce(
    (sum, item) => sum + (Number(item.percentage) || 0),
    0,
  );
  const contractSumDisplay =
    normalizeMoney(form.contractSum) ||
    normalizeMoney(String(milestoneTotal)) ||
    "$0.00";
  const insuranceCoverageSelected =
    form.insuranceCommercialGeneralLiability !== null ||
    form.insuranceSelfInsured ||
    form.insuranceWorkersCompensation;
  const reviewChecklist = [
    {
      title: "Project details",
      complete: Boolean(
        form.projectName &&
          form.projectSite &&
          form.projectZip &&
          form.projectCity &&
          form.projectState &&
          form.projectCountry &&
          form.primeContractDate &&
          form.commencementDate &&
          form.completionDate,
      ),
      detail: `${form.projectName || "Project name pending"} · ${form.projectSite || "Project site pending"}${form.projectZip ? ` · ${form.projectZip}` : ""}`,
      step: 2,
    },
    {
      title: "Contractor / GC details",
      complete: Boolean(
        form.contractorCompany &&
          form.contractorName &&
          form.contractorEmail &&
          isValidPhone(form.contractorPhone),
      ),
      detail: `${form.contractorCompany || "Company pending"} · ${form.contractorEmail || "Email pending"}`,
      step: 3,
    },
    {
      title: "Subcontractor details",
      complete: Boolean(
        form.subcontractorCompany &&
          form.subcontractorName &&
          form.subcontractorEmail &&
          isValidPhone(form.subcontractorPhone),
      ),
      detail: `${form.subcontractorCompany || "Company pending"} · ${form.subcontractorEmail || "Email pending"}`,
      step: 1,
    },
    {
      title: "Scope and responsibilities",
      complete:
        form.creatorRole === "contractor"
          ? Boolean(form.legacyResponsibilities.length)
          : true,
      detail:
        form.creatorRole === "contractor"
          ? `${form.legacyResponsibilities.length} responsibilities selected${scopeDocumentUrl ? " · original scope attached" : ""}`
          : `Responsibilities are included in the agreement${scopeDocumentUrl ? " · original scope attached" : ""}`,
      step: 2,
    },
    {
      title: "Milestones",
      complete: Boolean(milestones.length && milestonePercentageTotal === 100),
      detail: `${milestones.length} milestones · ${milestonePercentageTotal}% / 100% · ${contractSumDisplay}`,
      step: 4,
    },
    {
      title: "Insurance coverage",
      complete: Boolean(
        insuranceCoverageSelected &&
          (form.insuranceCommercialGeneralLiability !== true ||
            form.insuranceCompanyName.trim()),
      ),
      detail:
        form.insuranceCommercialGeneralLiability === true
          ? `CGL carrier: ${form.insuranceCompanyName || "company name pending"}`
          : form.insuranceCommercialGeneralLiability === false
            ? "Contractor does not carry commercial general liability insurance"
            : form.insuranceSelfInsured
              ? "Contractor is self-insured"
              : form.insuranceWorkersCompensation
                ? "Contractor carries Workers Compensation Insurance"
                : "Coverage option pending",
      step: 4,
    },
  ];

  const update = (field: keyof AgreementForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const updateCheckbox = (
    field: keyof AgreementForm,
    checked: boolean | null,
  ) => {
    setForm((current) => ({ ...current, [field]: checked }));
  };
  const lookupProjectZip = async (zip: string) => {
    const normalizedZip = zip.replace(/\D/g, "").slice(0, 5);
    update("projectZip", normalizedZip);
    setProjectZipError("");

    if (normalizedZip.length !== 5) {
      setForm((current) => ({
        ...current,
        projectZip: normalizedZip,
        projectCity: "",
        projectState: "",
        projectCountry: "",
      }));
      return;
    }

    setProjectZipLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/misc/zipcode/US/${normalizedZip}`,
      );
      if (!response.ok) throw new Error("Zip Code not found");
      const data = await response.json();
      const place = data?.places?.[0];
      if (!place) throw new Error("Zip Code not found");
      setForm((current) => ({
        ...current,
        projectZip: normalizedZip,
        projectCity: String(place["place name"] || "").replace("City", ""),
        projectState: place["state abbreviation"] || place.state || "",
        projectCountry: data["country abbreviation"] || data.country || "US",
      }));
    } catch (error: any) {
      setProjectZipError(error?.message || "Zip Code not found");
      setForm((current) => ({
        ...current,
        projectZip: normalizedZip,
        projectCity: "",
        projectState: "",
        projectCountry: "",
      }));
    } finally {
      setProjectZipLoading(false);
    }
  };
  const updateLegacyResponsibility = (id: string, checked: boolean) => {
    setForm((current) => ({
      ...current,
      legacyResponsibilities: checked
        ? Array.from(new Set([...current.legacyResponsibilities, id]))
        : current.legacyResponsibilities.filter((item) => item !== id),
    }));
  };
  const applyPartyToOtherSide = (party: ContractPartyPrefill) => {
    setUseExternalParty(false);
    setSelectedRegisteredParty(party);
    setForm((current) => {
      if (current.creatorRole === "contractor") {
        return {
          ...current,
          subcontractorUserId: String(party.userId || ""),
          subcontractorTitlePrefix:
            party.titlePrefix || current.subcontractorTitlePrefix,
          subcontractorCompany: party.company || current.subcontractorCompany,
          subcontractorName: party.name || current.subcontractorName,
          subcontractorEmail: party.email || current.subcontractorEmail,
          subcontractorPhone: party.phone || current.subcontractorPhone,
          subcontractorAddress: party.address || current.subcontractorAddress,
          licenseInfo: current.licenseInfo || party.licenseInfo,
        };
      }

      return {
        ...current,
        contractorUserId: String(party.userId || ""),
        contractorTitlePrefix:
          party.titlePrefix || current.contractorTitlePrefix,
        contractorCompany: party.company || current.contractorCompany,
        contractorName: party.name || current.contractorName,
        contractorEmail: party.email || current.contractorEmail,
        contractorPhone: party.phone || current.contractorPhone,
        contractorAddress: party.address || current.contractorAddress,
        licenseInfo: current.licenseInfo || party.licenseInfo,
      };
    });
    toast.success("Registered profile selected.");
  };

  const searchRegisteredParty = async (
    role: "contractor" | "subcontractor" | "owner" = "contractor",
  ) => {
    const query =
      role === "owner" ? ownerSearchQuery.trim() : partySearchQuery.trim();
    if (query.length < 2) {
      role === "owner"
        ? setOwnerSearchError("Enter at least 2 characters to search.")
        : setPartySearchError("Enter at least 2 characters to search.");
      return;
    }

    if (role === "owner") {
      setOwnerSearchLoading(true);
      setOwnerSearchError("");
      setOwnerSearchResults([]);
    } else {
      setPartySearchLoading(true);
      setPartySearchError("");
      setPartySearchResults([]);
    }

    try {
      const response: any = await CallAPIGetPromise(
        `/contract/subcontract-agreements/party-search?q=${encodeURIComponent(query)}&role=${role}`,
      );
      const results = unwrapApiData(response) || [];
      role === "owner"
        ? setOwnerSearchResults(results)
        : setPartySearchResults(results);
      if (!results.length) {
        const message =
          role === "owner"
            ? "No registered owner/client found. Invite them first, then select their profile."
            : `No registered ${role === "subcontractor" ? "subcontractor" : "contractor / GC"} found. Invite them first, then select their profile.`;
        role === "owner"
          ? setOwnerSearchError(message)
          : setPartySearchError(message);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Search failed. Please try again.";
      role === "owner"
        ? setOwnerSearchError(message)
        : setPartySearchError(message);
    } finally {
      role === "owner"
        ? setOwnerSearchLoading(false)
        : setPartySearchLoading(false);
    }
  };

  const applyOwner = (party: ContractPartyPrefill) => {
    setSelectedOwner(party);
    setForm((current) => ({
      ...current,
      ownerUserId: String(party.userId || ""),
      ownerTitlePrefix: party.titlePrefix || current.ownerTitlePrefix,
      ownerName: party.name || party.company || current.ownerName,
      ownerEmail: party.email || current.ownerEmail,
      ownerPhone: party.phone || current.ownerPhone,
    }));
    toast.success("Owner/client profile selected.");
  };

  const inviteUser = async (role: "contractor" | "subcontractor" | "owner") => {
    if (!inviteEmail.trim()) {
      toast.warning("Enter an email to invite.");
      return;
    }
    try {
      const response: any = await CallAPIPostPromise(
        "/contract/subcontract-agreements/invite-party",
        {
          email: inviteEmail.trim(),
          role,
        },
      );
      toast.success(response?.message || "Invite sent.");
      setInviteEmail("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Unable to send invite.");
    }
  };

  const selectCreatorRole = (role: CreatorRole) => {
    if (role === "contractor") {
      setForm(
        applyPrefillToParty(
          getBlankContractorCreatorForm(),
          "contractor",
          currentUserPrefill,
        ),
      );
      setMilestones([]);
      setMilestoneDraft({ ...emptyMilestone });
      setMilestoneEditorOpen(true);
      setEditingMilestoneIndex(null);
      setScopeDocumentUrl("");
      setAiExtractedContractSum("");
      setFiguresAcknowledged(false);
      setAiAmountVerified(false);
    } else {
      setForm((current) => {
        let next: AgreementForm = { ...current, creatorRole: "subcontractor" };
        const targetParty: CreatorRole = "subcontractor";
        const otherParty: CreatorRole = "contractor";

        if (partyMatchesPrefill(next, otherParty, currentUserPrefill)) {
          next = clearParty(next, otherParty);
        }

        return applyPrefillToParty(next, targetParty, currentUserPrefill);
      });
    }
    setPartySearchQuery("");
    setPartySearchResults([]);
    setPartySearchError("");
    setSelectedRegisteredParty(null);
    setUseExternalParty(false);
    setSelectedOwner(null);
    setOwnerSearchQuery("");
    setOwnerSearchResults([]);
    setOwnerSearchError("");
  };

  const calculateMilestoneAmount = (
    percentage: string,
    contractSum = form.contractSum,
  ) => {
    const total = Number(String(contractSum || "").replace(/,/g, "")) || 0;
    return ((total * (Number(percentage) || 0)) / 100).toFixed(2);
  };

  const applyContractSum = (contractSum: string) => {
    setForm((current) => ({ ...current, contractSum }));
    setMilestones((current) =>
      current.map((item) => ({
        ...item,
        amount: calculateMilestoneAmount(item.percentage, contractSum),
      })),
    );
    setMilestoneDraft((current) => ({
      ...current,
      amount: calculateMilestoneAmount(current.percentage, contractSum),
    }));
  };

  const updateMilestoneDraft = (
    field: "title" | "description" | "percentage",
    value: string,
  ) => {
    const nextValue =
      field === "percentage"
        ? value === ""
          ? ""
          : String(
              Math.min(
                100,
                Math.max(0, Number(value.replace(/[^\d.]/g, "")) || 0),
              ),
            )
        : value;
    setMilestoneDraft((current) => ({
      ...current,
      [field]: nextValue,
      ...(field === "percentage"
        ? { amount: calculateMilestoneAmount(nextValue) }
        : {}),
    }));
  };

  const uploadScopeDocument = async (file: File) => {
    if (file.type !== "application/pdf") {
      toast.warning("Please upload a PDF document.");
      return;
    }
    setScopeUploading(true);
    setShowScopeExtraction(true);
    setScopeExtractionStage("uploading");
    setExtractedScopePreview("");
    try {
      const url = await dispatch(sendDocToS3("SubcontractScope", file));
      setScopeDocumentUrl(String(url || ""));
      setScopeExtractionStage("routing");

      const extractionResponse = await fetch(`${apiUrl}/misc/document-ai-v5`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!extractionResponse.body)
        throw new Error(
          "The extraction service did not return a document stream.",
        );

      const reader = extractionResponse.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let extracted = "";
      let extractedContractSum = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split("\n\n");
        buffer = messages.pop() || "";

        for (const message of messages) {
          if (!message.startsWith("data:")) continue;
          const event = message.replace("data:", "").trim();
          if (event === "[DONE]") {
            setScopeExtractionStage("complete");
            continue;
          }
          const parsed = JSON.parse(event);
          if (parsed.status === "routed" || parsed.engine)
            setScopeExtractionStage("processing");
          if (parsed.status === "extracted" || parsed.extraction) {
            const amount = normalizeExtractedAmount(
              parsed.extraction?.totalAmount ?? parsed.totalAmount,
            );
            if (amount) {
              extractedContractSum = amount;
              setAiExtractedContractSum(amount);
              setAiAmountVerified(false);
              applyContractSum(amount);
            }
          }
          if (parsed.chunk) {
            setScopeExtractionStage("streaming");
            extracted += parsed.chunk;
            setExtractedScopePreview(extracted.replace(/```html|```/gi, ""));
          }
          if (parsed.status === "done") setScopeExtractionStage("complete");
        }
      }

      const cleanedHtml = extracted.replace(/```html|```/gi, "").trim();
      if (richTextToPlainText(cleanedHtml)) update("scopeOfWork", cleanedHtml);
      setScopeExtractionStage("complete");
      toast.success(
        extractedContractSum
          ? `Scope document attached. Contract Sum set to ${normalizeMoney(extractedContractSum)}.`
          : "Scope document attached and Scope of Work extracted.",
      );
    } catch (error: any) {
      toast.error(
        error?.message ||
          "Unable to upload or extract the Scope of Work document.",
      );
    } finally {
      setScopeUploading(false);
      window.setTimeout(() => setShowScopeExtraction(false), 700);
    }
  };

  const openNewMilestone = () => {
    if (milestonePercentageTotal >= 100) {
      toast.info(
        "Milestones already total 100%. Edit or delete an existing milestone to add another one.",
      );
      return;
    }
    setEditingMilestoneIndex(null);
    setMilestoneDraft({ ...emptyMilestone });
    setMilestoneEditorOpen(true);
  };

  const editMilestone = (index: number) => {
    setEditingMilestoneIndex(index);
    setMilestoneDraft({ ...milestones[index] });
    setMilestoneEditorOpen(true);
  };

  const closeMilestoneEditor = () => {
    setEditingMilestoneIndex(null);
    setMilestoneDraft({ ...emptyMilestone });
    setMilestoneEditorOpen(false);
  };

  const saveMilestone = () => {
    const percentage = Number(milestoneDraft.percentage);
    const percentageWithoutCurrent = milestones.reduce(
      (sum, item, index) =>
        sum +
        (index === editingMilestoneIndex ? 0 : Number(item.percentage) || 0),
      0,
    );
    if (!milestoneDraft.title.trim() || percentage <= 0) {
      toast.warning("Enter a milestone name and percentage.");
      return;
    }
    if (percentageWithoutCurrent + percentage > 100) {
      toast.warning(
        `Only ${Math.max(0, 100 - percentageWithoutCurrent)}% is available.`,
      );
      return;
    }
    const savedMilestone = {
      ...milestoneDraft,
      title: milestoneDraft.title.trim(),
      amount: calculateMilestoneAmount(milestoneDraft.percentage),
    };
    setMilestones((current) =>
      editingMilestoneIndex === null
        ? [...current, savedMilestone]
        : current.map((item, index) =>
            index === editingMilestoneIndex ? savedMilestone : item,
          ),
    );
    closeMilestoneEditor();
    toast.success(
      editingMilestoneIndex === null
        ? "Milestone added."
        : "Milestone updated.",
    );
  };

  const removeMilestone = (index: number) => {
    setMilestones((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
    if (editingMilestoneIndex === index) closeMilestoneEditor();
  };

  const canContinue = () => {
    if (step === 1) {
      return (
        form.creatorRole === "contractor" ||
        form.creatorRole === "subcontractor"
      );
    }
    if (step === 2) {
      const hasRequiredContractorCompletionOptions =
        form.creatorRole !== "contractor" ||
        Boolean(
          form.completionDateOption &&
            (form.completionDateOption !== "specificDate" ||
              form.completionDateSpecific) &&
            (form.completionDateOption !== "other" ||
              form.completionDateOther) &&
            form.substantialCompletionDateOption &&
            (form.substantialCompletionDateOption !== "specificDate" ||
              form.substantialCompletionDateSpecific) &&
            (form.substantialCompletionDateOption !== "other" ||
              form.substantialCompletionDateOther),
        );
      return Boolean(
        form.projectName &&
          form.projectSite &&
          form.projectZip &&
          form.projectCity &&
          form.projectState &&
          form.projectCountry &&
          form.primeContractDate &&
          form.commencementDate &&
          form.completionDate &&
          new Date(form.completionDate) >= new Date(form.commencementDate) &&
          hasRequiredContractorCompletionOptions,
      );
    }
    if (step === 3) {
      const targetParty =
        form.creatorRole === "contractor" ? "subcontractor" : "contractor";
      const hasPartyDetails =
        targetParty === "contractor"
          ? Boolean(
              form.contractorCompany &&
                form.contractorName &&
                form.contractorEmail &&
                isValidPhone(form.contractorPhone),
            )
          : Boolean(
              form.subcontractorCompany &&
                form.subcontractorName &&
                form.subcontractorEmail &&
                isValidPhone(form.subcontractorPhone),
            );
      return Boolean(
        selectedRegisteredParty || (useExternalParty && hasPartyDetails),
      );
    }
    if (step === 4) {
      const hasRequiredContractorPaymentTerms =
        form.creatorRole !== "contractor" ||
        Boolean(
          form.paymentAmountOption &&
            (form.paymentAmountOption !== "hourlyRate" ||
              form.paymentAmountHourlyRate.trim()) &&
            (form.paymentAmountOption !== "other" ||
              form.paymentAmountOtherDescription.trim()) &&
            form.paymentMethodOption &&
            (form.paymentMethodOption !== "other" ||
              form.paymentMethodOther.trim()) &&
            (form.paymentMethodOption !== "regular" ||
              form.paymentFrequencyOption) &&
            (form.paymentMethodOption !== "regular" ||
              form.paymentFrequencyOption !== "other" ||
              form.paymentFrequencyOther.trim()) &&
            form.rightToSubcontracting &&
            form.assignmentRight,
        );
      return Boolean(
        isValidAmount(form.contractSum) &&
          milestones.length &&
          milestonePercentageTotal === 100 &&
          milestones.every(
            (milestone) =>
              milestone.title.trim() &&
              isValidAmount(milestone.amount) &&
              Number(milestone.percentage) > 0,
          ) &&
          hasRequiredContractorPaymentTerms,
      );
    }
    return true;
  };

  const goToStep = (nextStep: number) => {
    const allowedNextStep = Math.max(minimumStep, nextStep);
    if (generatedAgreement) {
      setStep(allowedNextStep);
      setShowStepValidation(false);
      return;
    }
    if (allowedNextStep <= step) {
      setStep(allowedNextStep);
      setShowStepValidation(false);
      return;
    }
    if (!canContinue()) {
      setShowStepValidation(true);
      toast.warning(
        step === 4 && milestonePercentageTotal !== 100
          ? "Milestone percentages must total exactly 100% before continuing."
          : "Please complete this tab before continuing.",
      );
      focusFirstRequiredField();
      return;
    }
    setStep(Math.min(allowedNextStep, step + 1));
  };

  const focusFirstRequiredField = () => {
    window.requestAnimationFrame(() => {
      if (
        step === 4 &&
        milestonePercentageTotal !== 100 &&
        milestoneWarningRef.current
      ) {
        milestoneWarningRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        window.setTimeout(
          () => milestoneWarningRef.current?.focus({ preventScroll: true }),
          350,
        );
        return;
      }
      const wizard = document.querySelector(".subcontract-wizard-card");
      const target =
        wizard?.querySelector<HTMLElement>(
          ".is-invalid, input[required]:invalid, textarea[required]:invalid, select[required]:invalid",
        ) ||
        (step === 3 && !selectedRegisteredParty && !useExternalParty
          ? wizard?.querySelector<HTMLElement>("[data-party-search]")
          : null);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => {
        target.focus({ preventScroll: true });
      }, 350);
    });
  };

  const next = () => {
    if (!canContinue()) {
      setShowStepValidation(true);
      toast.warning(
        step === 4 && milestonePercentageTotal !== 100
          ? "Milestone percentages must total exactly 100% before continuing."
          : "Please fill required fields before continuing.",
      );
      focusFirstRequiredField();
      return;
    }
    setShowStepValidation(false);
    setStep((current) => Math.min(current + 1, 5));
  };
  const back = () => setStep((current) => Math.max(current - 1, minimumStep));

  const submit = async (mode: "draft" | "send") => {
    if (mode === "send") {
      if (!figuresAcknowledged) {
        toast.warning(
          "Please acknowledge the figures and liability waiver before generating.",
        );
        return;
      }
      if (aiExtractedContractSum && !aiAmountVerified) {
        toast.warning(
          "Please verify the AI-filled Contract Sum amount before generating.",
        );
        return;
      }
    }
    setSaving(mode);
    try {
      const formForPayload =
        form.creatorRole === "subcontractor"
          ? applyPrefillToParty(form, "subcontractor", currentUserPrefill)
          : form;
      const endpoint = editAgreementId
        ? `/contract/subcontract-agreements/${editAgreementId}/${mode === "draft" ? "draft" : "send"}`
        : mode === "draft"
          ? "/contract/subcontract-agreements/draft"
          : "/contract/subcontract-agreements/send";
      const payload = toPayload(
        {
          ...formForPayload,
          contractSum: form.contractSum || String(milestoneTotal || ""),
        },
        milestones,
        scopeDocumentUrl,
      );
      if (changeOrderFor) {
        (payload as any).changeOrderFor = changeOrderFor;
      }
      if (hicProjectId) {
        (payload as any).hicProjectId = hicProjectId;
      }
      const response: any = editAgreementId
        ? await CallAPIPutPromise(endpoint, payload)
        : await CallAPIPostPromise(endpoint, payload);
      if (mode === "send" && response?.data?.id) {
        const pdfResponse: any = await CallAPIPostPromise(
          `/contract/subcontract-agreements/${response.data.id}/generate-pdf`,
          { sendGuestInvite: true },
        );
        const generated = pdfResponse?.data || response?.data;
        setGeneratedAgreement(generated);
        setLoadedAgreement(generated);
        setStep(5);
        toast.success("Contract generated successfully.");
        if (generated?.canSign) {
          setShowSignatureModal(true);
        }
        return;
      }
      toast.success(response?.message || "Draft saved");
      navigate(
        hicProjectId
          ? `/create-contract?type=project&tab=Subcontractor&id=${hicProjectId}&action=1`
          : "/view-sub-contracts",
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Unable to save subcontract agreement.",
      );
    } finally {
      setSaving(null);
    }
  };

  if (loadingCapabilities) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        </Container>
      </div>
    );
  }

  if (!capabilities?.canCreateSubcontractAgreement && !editAgreementId) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="warning" className="d-flex gap-3 align-items-start">
            <Lock className="mt-1" size={20} />
            <div>
              <h6 className="mb-1">Subscription required</h6>
              <div>
                {capabilities?.reason ||
                  "Active contractor subscription required to create subcontract agreements."}
              </div>
              <Button
                className="mt-3"
                variant="primary"
                onClick={() => navigate("/contract-tutorial-and-plans")}
              >
                View Plans
              </Button>
            </div>
          </Alert>
        </Container>
      </div>
    );
  }

  const renderOtherDetails = () => {
    const isOtherContractor = form.creatorRole === "subcontractor";
    const otherRole = isOtherContractor ? "contractor" : "subcontractor";
    const otherRoleLabel = isOtherContractor
      ? "contractor / GC"
      : "subcontractor";
    return (
      <>
        <Card className="border bg-white mb-4">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
              <div>
                <h6 className="mb-1">Search registered {otherRoleLabel}</h6>
              </div>
              {selectedRegisteredParty ? (
                <Badge bg="success">Registered profile selected</Badge>
              ) : null}
            </div>

            <div className="profile-search-row">
              <Form.Control
                data-party-search
                className="profile-search-input"
                value={partySearchQuery}
                onChange={(e) => setPartySearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    searchRegisteredParty(otherRole);
                  }
                }}
                placeholder="Search by name, email, or company"
              />
              <Button
                type="button"
                variant="primary"
                className="profile-search-button"
                disabled={partySearchLoading}
                onClick={() => searchRegisteredParty(otherRole)}
              >
                {partySearchLoading ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <Search size={16} className="me-2" />
                )}
                Search
              </Button>
            </div>

            {partySearchError ? (
              <Alert variant="warning" className="py-2 mt-3 mb-0 small">
                {partySearchError}
              </Alert>
            ) : null}

            {partySearchResults.length ? (
              <div className="profile-result-list">
                {partySearchResults.map((party) => (
                  <Card
                    key={`${party.userId || party.email}-${party.companyId || "profile"}`}
                    className={`profile-result-card ${selectedRegisteredParty?.email === party.email ? "selected" : ""}`}
                    onClick={() => applyPartyToOtherSide(party)}
                    role="button"
                  >
                    <Card.Body className="py-2 px-3">
                      <div className="profile-result-row">
                        <div className="min-w-0">
                          <div className="fw-semibold">
                            {party.company ||
                              party.name ||
                              `Registered ${otherRoleLabel}`}
                          </div>
                          <div className="small text-muted">
                            {party.name || "Contact pending"} ·{" "}
                            {party.email || "Email pending"}{" "}
                            {party.phone ? `· ${party.phone}` : ""}
                          </div>
                          {party.address ? (
                            <div className="small text-muted">
                              {party.address}
                            </div>
                          ) : null}
                        </div>
                        <Button
                          size="sm"
                          className="profile-use-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            applyPartyToOtherSide(party);
                          }}
                        >
                          {selectedRegisteredParty?.email === party.email ? (
                            <CheckCircle2 size={15} className="me-1" />
                          ) : (
                            <UserPlus size={15} className="me-1" />
                          )}
                          {selectedRegisteredParty?.email === party.email
                            ? "Selected"
                            : "Select"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            ) : null}
          </Card.Body>
        </Card>

        {selectedRegisteredParty ? (
          <Card className="selected-party-card border-success-subtle mb-3">
            <Card.Body className="p-4">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="selected-party-icon">
                      <CheckCircle2 size={18} />
                    </span>
                    <div>
                      <div className="fw-semibold fs-5">
                        {selectedRegisteredParty.company ||
                          selectedRegisteredParty.name}
                      </div>
                      <div className="small text-muted">
                        Registered {otherRoleLabel} profile
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="success">Selected from system</Badge>
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={() => {
                      setSelectedRegisteredParty(null);
                      setForm((current) =>
                        clearParty(
                          current,
                          isOtherContractor ? "contractor" : "subcontractor",
                        ),
                      );
                    }}
                  >
                    Change
                  </Button>
                </div>
              </div>

              <div className="selected-party-grid">
                <div>
                  <span className="selected-party-label">Contact</span>
                  <strong>
                    {selectedRegisteredParty.name || "Contact pending"}
                  </strong>
                </div>
                <div>
                  <span className="selected-party-label">Email</span>
                  <strong>
                    {selectedRegisteredParty.email || "Email pending"}
                  </strong>
                </div>
                <div>
                  <span className="selected-party-label">Mobile</span>
                  <strong>
                    {selectedRegisteredParty.phone || "Phone pending"}
                  </strong>
                  <small className="text-success d-block mt-1">
                    <Lock size={12} className="me-1" />
                    Locked from system profile
                  </small>
                </div>
                <div>
                  <span className="selected-party-label">Address</span>
                  <strong>
                    {selectedRegisteredParty.address || "Address pending"}
                  </strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        ) : useExternalParty ? (
          <Card className="border border-primary-subtle mb-3">
            <Card.Body>
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <h6 className="mb-1">Add external {otherRoleLabel}</h6>
                  <p className="small text-muted mb-0">
                    They will receive a secure email link to review and sign. No
                    account is required.
                  </p>
                </div>
                <Badge bg="primary">Guest signer</Badge>
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <Field label="Company / Business Name" required>
                    <Form.Control
                      required
                      value={
                        isOtherContractor
                          ? form.contractorCompany
                          : form.subcontractorCompany
                      }
                      onChange={(e) =>
                        update(
                          isOtherContractor
                            ? "contractorCompany"
                            : "subcontractorCompany",
                          e.target.value,
                        )
                      }
                    />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Contact Person" required>
                    <Form.Control
                      required
                      value={
                        isOtherContractor
                          ? form.contractorName
                          : form.subcontractorName
                      }
                      onChange={(e) =>
                        update(
                          isOtherContractor
                            ? "contractorName"
                            : "subcontractorName",
                          e.target.value,
                        )
                      }
                    />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Email" required>
                    <Form.Control
                      required
                      type="email"
                      value={
                        isOtherContractor
                          ? form.contractorEmail
                          : form.subcontractorEmail
                      }
                      onChange={(e) =>
                        update(
                          isOtherContractor
                            ? "contractorEmail"
                            : "subcontractorEmail",
                          e.target.value,
                        )
                      }
                    />
                  </Field>
                </Col>
                <Col md={6}>
                  <Field label="Mobile Number" required>
                    <Form.Control
                      required
                      type="tel"
                      inputMode="tel"
                      isInvalid={Boolean(
                        (isOtherContractor
                          ? form.contractorPhone
                          : form.subcontractorPhone) &&
                          !isValidPhone(
                            isOtherContractor
                              ? form.contractorPhone
                              : form.subcontractorPhone,
                          ),
                      )}
                      value={
                        isOtherContractor
                          ? form.contractorPhone
                          : form.subcontractorPhone
                      }
                      onChange={(e) =>
                        update(
                          isOtherContractor
                            ? "contractorPhone"
                            : "subcontractorPhone",
                          formatPhoneInput(e.target.value),
                        )
                      }
                      placeholder="(555) 123-4567"
                    />
                    <Form.Control.Feedback type="invalid">
                      Enter a valid 10-digit mobile number.
                    </Form.Control.Feedback>
                  </Field>
                </Col>
                {!isOtherContractor ? (
                  <Col md={6}>
                    <Field label="License / Trade">
                      <Form.Control
                        value={form.licenseInfo}
                        onChange={(e) => update("licenseInfo", e.target.value)}
                        placeholder="Select or enter trade"
                      />
                    </Field>
                  </Col>
                ) : null}
                <Col xs={12}>
                  <Field label="Business Address">
                    <Form.Control
                      value={
                        isOtherContractor
                          ? form.contractorAddress
                          : form.subcontractorAddress
                      }
                      onChange={(e) =>
                        update(
                          isOtherContractor
                            ? "contractorAddress"
                            : "subcontractorAddress",
                          e.target.value,
                        )
                      }
                    />
                  </Field>
                </Col>
              </Row>
              <Button
                type="button"
                variant="link"
                className="px-0 mt-2"
                onClick={() => {
                  setUseExternalParty(false);
                  setForm((current) =>
                    clearParty(
                      current,
                      isOtherContractor ? "contractor" : "subcontractor",
                    ),
                  );
                }}
              >
                Choose a registered profile instead
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Alert variant="info" className="small">
            Search for an existing profile, or add them as a guest signer if
            they are not in the system.
          </Alert>
        )}

        {!selectedRegisteredParty && !useExternalParty ? (
          <Card className="border bg-white">
            <Card.Body>
              <h6 className="mb-1">Not in The Tradesmen’s Collective?</h6>
              <p className="text-muted small mb-3">
                Add their details now. We’ll email a private signing link and
                show them the optional benefits of joining after they review the
                agreement.
              </p>
              <Button
                type="button"
                variant="outline-primary"
                onClick={() => {
                  setUseExternalParty(true);
                  setSelectedRegisteredParty(null);
                }}
              >
                <UserPlus size={15} className="me-1" /> Add as guest signer
              </Button>
            </Card.Body>
          </Card>
        ) : null}
      </>
    );
  };

  // A subcontract change order is deliberately a short addendum flow.  The
  // parties and project are inherited from the executed parent agreement, so
  // asking the user to step through the full agreement wizard again is both
  // unnecessary and easy to get wrong.
  if (isChangeOrder) {
    const requiredRemaining = [form.title, form.scopeOfWork, form.contractSum]
      .filter((value) => !String(value || "").trim()).length;
    const originalContractSum = normalizeMoney(
      String(loadedAgreement?.contractSum || loadedAgreement?.payload?.terms?.contractSum || ""),
    ) || "—";
    const projectAddress = [
      form.projectSite,
      form.projectCity,
      form.projectState,
      form.projectZip,
    ].filter(Boolean).join(", ");
    const canGenerate = requiredRemaining === 0 && figuresAcknowledged;

    return (
      <div className="page-content contract-flow-page subcontract-page-shell tailwind-prefix">
        <Container fluid>
          <style>{`
            .subcontract-page-shell { background: #f5f7f9; min-height: 100vh; }
            .subcontract-change-shell { max-width: 1180px; margin: 0 auto; padding: 8px 0 150px; }
            .subcontract-change-card { border: 1px solid #dfe5ec; border-radius: 14px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,.03); padding: 20px; margin-bottom: 16px; }
            .subcontract-change-field { border: 1px solid #e4e9f0; background: #f7f8fa; border-radius: 9px; padding: 12px; }
            .subcontract-change-label { color: #667085; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
            .subcontract-change-value { color: #111827; font-size: 14px; font-weight: 600; margin-top: 4px; overflow-wrap: anywhere; }
            .subcontract-change-actions { position: sticky; bottom: 18px; z-index: 20; border: 1px solid #dfe5ec; border-radius: 12px; background: rgba(255,255,255,.96); padding: 14px 16px; box-shadow: 0 -8px 24px rgba(15,23,42,.10); backdrop-filter: blur(8px); }
            @media (min-width: 992px) { .subcontract-change-actions { margin-right: 300px; } }
          `}</style>
          <div className="subcontract-change-shell">
            <div className="subcontract-change-card">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="d-inline-flex align-items-center gap-2 rounded bg-primary-subtle text-primary px-2 py-1 small fw-semibold">
                    <ClipboardCheck size={15} /> Change Order
                  </div>
                  <h3 className="mt-3 mb-1 fw-semibold">Add Subcontract Change Order</h3>
                  <p className="mb-0 text-muted">Review the inherited agreement details, then record the approved work, amount, and schedule change.</p>
                </div>
                <div className="rounded border bg-light px-3 py-2 text-center">
                  <div className="subcontract-change-label">Required remaining</div>
                  <div className="fs-4 fw-semibold">{requiredRemaining}</div>
                </div>
              </div>
            </div>

            <Row className="g-3 mb-3">
              <Col lg={6}><div className="subcontract-change-card h-100"><div className="d-flex gap-3 mb-3"><div className="subcontract-section-icon"><UsersRound size={20} /></div><div><h5 className="mb-1">Contractor / GC</h5><p className="text-muted small mb-0">Inherited from the signed agreement.</p></div></div><div className="subcontract-change-field"><div className="subcontract-change-label">Contractor</div><div className="subcontract-change-value">{form.contractorCompany || form.contractorName || "—"}</div><div className="small text-muted">{form.contractorName}{form.contractorEmail ? ` · ${form.contractorEmail}` : ""}</div></div></div></Col>
              <Col lg={6}><div className="subcontract-change-card h-100"><div className="d-flex gap-3 mb-3"><div className="subcontract-section-icon"><UsersRound size={20} /></div><div><h5 className="mb-1">Subcontractor</h5><p className="text-muted small mb-0">Signing party stays the same for this addendum.</p></div></div><div className="subcontract-change-field"><div className="subcontract-change-label">Subcontractor</div><div className="subcontract-change-value">{form.subcontractorCompany || form.subcontractorName || "—"}</div><div className="small text-muted">{form.subcontractorName}{form.subcontractorEmail ? ` · ${form.subcontractorEmail}` : ""}</div></div></div></Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col lg={7}><div className="subcontract-change-card h-100"><div className="d-flex gap-3 mb-3"><div className="subcontract-section-icon"><MapPin size={20} /></div><div><h5 className="mb-1">Project</h5><p className="text-muted small mb-0">Project information is locked to the original agreement.</p></div></div><div className="subcontract-change-field"><div className="subcontract-change-label">Project / Site</div><div className="subcontract-change-value">{form.projectName || "—"}</div><div className="small text-muted">{projectAddress || "Address not available"}</div></div></div></Col>
              <Col lg={5}><div className="subcontract-change-card h-100"><div className="d-flex gap-3 mb-3"><div className="subcontract-section-icon"><DollarSign size={20} /></div><div><h5 className="mb-1">Original Contract Value</h5><p className="text-muted small mb-0">Read-only context from the parent agreement.</p></div></div><div className="subcontract-change-field"><div className="subcontract-change-label">Original agreement amount</div><div className="fs-4 fw-semibold mt-1">{originalContractSum}</div></div></div></Col>
            </Row>

            <div className="subcontract-change-card">
              <div className="d-flex gap-3 mb-4"><div className="subcontract-section-icon"><ClipboardCheck size={20} /></div><div><h5 className="mb-1">Scope of Work & Accounting Summary</h5><p className="text-muted small mb-0">Describe the approved change and its cost. The original agreement remains otherwise unchanged.</p></div></div>
              <Row className="g-3">
                <Col md={12}><Form.Group><Form.Label>Scope of Work *</Form.Label><div className={`rounded border bg-white p-1 ${!form.scopeOfWork.trim() ? "border-warning" : "border-light"}`}><RichEditorTipTab value={form.scopeOfWork} onChange={(content: string) => update("scopeOfWork", content)} /></div></Form.Group></Col>
                <Col md={6}><Form.Group><Form.Label>Amount of this change order *</Form.Label><Form.Control value={form.contractSum} inputMode="decimal" placeholder="0.00" onChange={(event) => { const value = event.target.value; if (/^[0-9,.]*$/.test(value)) update("contractSum", value); }} /></Form.Group></Col>
                <Col md={6}><Form.Group><Form.Label>Revised completion date <span className="text-muted">(optional)</span></Form.Label><div className="input-group"><span className="input-group-text"><Timer size={16} /></span><Form.Control type="date" min={form.primeContractDate || undefined} value={form.completionDate} onChange={(event) => update("completionDate", event.target.value)} /></div><Form.Text muted>Leave blank when the schedule is not changing.</Form.Text></Form.Group></Col>
              </Row>
            </div>

            <div className="subcontract-change-card">
              <div className="d-flex gap-3 mb-3"><div className="subcontract-section-icon"><FileText size={20} /></div><div><h5 className="mb-1">Document Name *</h5><p className="text-muted small mb-0">This title identifies the change order in its history and signing flow.</p></div></div>
              <Form.Control value={form.title} placeholder="Example: Additional electrical work" onChange={(event) => update("title", event.target.value)} />
            </div>

            {generatedAgreement ? (
              <div className="subcontract-change-card border-success"><div className="d-flex flex-wrap align-items-center justify-content-between gap-3"><div><h5 className="mb-1 text-success">Subcontract Change Order Ready</h5><p className="mb-0 text-muted">The PDF has been generated and is ready for signatures.</p></div><div className="d-flex gap-2">{generatedAgreement.contractUrl ? <a className="btn btn-outline-primary" href={generatedAgreement.contractUrl} target="_blank" rel="noreferrer"><Eye size={16} className="me-1" /> Preview PDF</a> : null}{generatedAgreement.canSign ? <Button onClick={() => openSignatureModal(generatedAgreement)}><FileSignature size={16} className="me-1" /> Sign now</Button> : null}</div></div></div>
            ) : (
              <div className="subcontract-change-actions"><div className="d-flex flex-wrap align-items-center justify-content-between gap-3"><label className="d-flex align-items-start gap-2 mb-0"><Form.Check checked={figuresAcknowledged} onChange={(event) => setFiguresAcknowledged(event.currentTarget.checked)} /><span className="small">I have reviewed the scope, amount, and schedule details for this change order.</span></label><Button variant="primary" disabled={!!saving || !canGenerate} onClick={() => submit("send")}>{saving === "send" ? <Spinner size="sm" className="me-2" /> : <FileText size={16} className="me-2" />}Generate Change Order</Button></div></div>
            )}
          </div>
          <ContractLoadingModal
            title="Subcontract Change Order Review and Signature"
            open={showSignatureModal}
            pdfURL={(generatedAgreement || loadedAgreement)?.contractUrl}
            project={null}
            sigCanvasRef={signatureRef}
            submitSignature={submitAgreementSignature}
            submitting={signing}
            submitLabel="Submit Signed Change Order"
            onClose={() => {
              if (!signing) setShowSignatureModal(false);
            }}
          />
          <SigningProgressModal status={signingStatus} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content contract-flow-page subcontract-page-shell">
      <Container fluid>
        <style>{`
          .subcontract-page-shell { background: #f5f7f9; min-height: 100vh; }
          .subcontract-wizard-card { width: 100%; max-width: none; margin: 0; scroll-margin-top: 92px; background: #f5f7f9; border: 0 !important; border-radius: 0; box-shadow: none !important; }
          .subcontract-wizard-card > .card-body { padding: 28px !important; }
          .subcontract-wizard-card .card { background: #fff; border-color: #dfe5ec; border-radius: 14px; box-shadow: none; }
          .subcontract-section-card { background: #fff; border: 1px solid #dfe5ec; border-radius: 14px; padding: 24px; margin-bottom: 22px; }
          .subcontract-section-card.compact { padding: 18px; }
          .subcontract-section-header { display: flex; gap: 14px; align-items: center; margin-bottom: 18px; min-height: 44px; }
          .subcontract-section-icon { width: 42px; height: 42px; border-radius: 10px; background: #eef4ff; color: #2f57d6; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
          .subcontract-section-header h4,
          .subcontract-section-header h5 { margin-bottom: 4px; font-size: 18px; line-height: 1.25; font-weight: 700; color: #111827; }
          .subcontract-section-header p { line-height: 1.35; font-size: 13px; color: #7c8397 !important; }
          .subcontract-wizard-card .form-label { font-size: 12px; line-height: 1.25; margin-bottom: 8px; color: #111827; }
          .subcontract-wizard-card .form-control,
          .subcontract-wizard-card .form-select { background: #f7f8fa; border-color: #e4e9f0; border-radius: 8px; min-height: 44px; font-size: 14px; line-height: 1.35; color: #18202a; }
          .subcontract-wizard-card .form-control::placeholder { color: #98a2b3; font-size: 14px; opacity: 1; }
          .subcontract-wizard-card textarea.form-control { min-height: auto; }
          .subcontract-wizard-card .form-control:focus,
          .subcontract-wizard-card .form-select:focus { background: #fff; border-color: #86a6ff; box-shadow: 0 0 0 .2rem rgba(47,87,214,.12); }
          .profile-search-row { display: grid; grid-template-columns: minmax(0, 1fr) 120px; gap: 12px; align-items: stretch; }
          .profile-search-input { min-height: 48px !important; font-size: 14px; }
          .profile-search-button { min-height: 48px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 14px; font-weight: 700; box-shadow: 0 8px 18px rgba(47,87,214,.16); }
          .profile-search-button svg { flex: 0 0 auto; }
          .profile-invite-button { min-height: 48px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 14px; font-weight: 700; background: #fff; }
          .profile-result-list { display: grid; gap: 10px; margin-top: 14px; }
          .profile-result-card { background: #fff; border: 1px solid #dbe3ef; border-radius: 10px; box-shadow: 0 6px 18px rgba(24,32,42,.04); cursor: pointer; transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease, background .18s ease; }
          .profile-result-card:hover { background: #fbfdff; border-color: #9db3f4; box-shadow: 0 10px 24px rgba(47,87,214,.09); transform: translateY(-1px); }
          .profile-result-card.selected { background: #f3fff8; border-color: #55d98f; box-shadow: 0 10px 24px rgba(16,160,95,.1); }
          .profile-result-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 18px; }
          .profile-result-row .min-w-0 { min-width: 0; }
          .profile-result-row .fw-semibold { font-size: 14px; line-height: 1.3; }
          .profile-result-row .small { overflow-wrap: anywhere; font-size: 12px; line-height: 1.4; }
          .profile-use-button { min-width: 96px; height: 34px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border: 1px solid #b8c8f6; border-radius: 8px; background: #eef4ff; color: #2f57d6; font-size: 13px; font-weight: 700; box-shadow: none; }
          .profile-result-card.selected .profile-use-button { background: #20c978; border-color: #20c978; color: #fff; }
          .profile-use-button:hover { background: #2f57d6; border-color: #2f57d6; color: #fff; }
          .subcontract-wizard-card .step-arrow-nav .nav { background: #f1f4f8; border: 1px solid #e3e9f0; border-radius: 10px; padding: 4px; }
          .subcontract-wizard-card .step-arrow-nav .nav-link { border-radius: 8px; }
          .insurance-options-list { display: grid; gap: 12px; }
          .insurance-options-list .form-check { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 0; }
          .insurance-options-list .form-check-input { margin-top: 3px; flex: 0 0 auto; }
          .insurance-options-list .form-check-label { font-size: 14px; line-height: 1.45; color: #18202a; }
          .subcontract-choice { border: 1px solid #e6edf5; background: #fff; transition: .2s ease; }
          .subcontract-choice:hover { border-color: #2f57d6; box-shadow: 0 12px 28px rgba(47,87,214,.08); }
          .subcontract-choice.active { border-color: #10a05f; background: #f3fff8; box-shadow: 0 14px 32px rgba(16,160,95,.1); }
          .selected-party-card { background: linear-gradient(180deg, #f2fff8 0%, #ffffff 100%); border: 1px solid #9ee6bf; box-shadow: 0 12px 30px rgba(16,160,95,.08); }
          .selected-party-icon { width: 38px; height: 38px; border-radius: 999px; background: #20c978; color: #fff; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
          .selected-party-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; border-top: 1px solid #bfeccc; padding-top: 16px; }
          .selected-party-grid > div { background: #fff; border: 1px solid #e6edf5; border-radius: 8px; padding: 12px 14px; min-width: 0; }
          .selected-party-grid strong { display: block; overflow-wrap: anywhere; }
          .selected-party-label { display: block; color: #7c8397; font-size: 12px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; }
          .milestone-percentage-summary { background: #f8fafc; border: 1px solid #dbe3ef; border-radius: 8px; margin-bottom: 18px; padding: 14px 16px; }
          .milestone-percentage-summary .progress { height: 9px; }
          .milestone-percentage-summary.incomplete { background: #fffaf0; border-color: #f0c36a; color: #8a5a00; }
          .milestone-percentage-summary:focus { outline: 3px solid rgba(47,87,214,.22); outline-offset: 3px; }
          .milestone-percentage-summary.complete { background: #f2fff8; border-color: #8ee0b4; color: #087443; }
          .milestone-percentage-summary.complete .progress-bar { background: #18a866; }
          .milestone-percentage-summary.over { background: #fff5f5; border-color: #f2b8b8; color: #b42318; }
          .milestone-percentage-summary.over .progress-bar { background: #dc3545; }
          .milestone-complete-inline { display: flex; align-items: center; justify-content: center; gap: 8px; color: #087443; font-weight: 600; font-size: 13px; margin-top: 12px; }
          .milestone-complete-inline span { width: 22px; height: 22px; border-radius: 999px; background: #e7fbf0; display: inline-flex; align-items: center; justify-content: center; }
          .subcontract-milestone-editor { border: 1px solid #dbe3ef; border-radius: 14px; background: #fff; padding: 24px; }
          .subcontract-timeline { position: relative; padding-left: 30px; }
          .subcontract-timeline::before { content: ""; position: absolute; left: 9px; top: 20px; bottom: 20px; width: 2px; background: #e4e9f1; }
          .subcontract-timeline-item { position: relative; border: 1px solid #dbe3ef; border-radius: 14px; background: #fff; padding: 20px; margin-bottom: 14px; }
          .subcontract-timeline-dot { position: absolute; left: -30px; top: 20px; width: 20px; height: 20px; border: 6px solid #edf1f7; border-radius: 50%; background: #fff; z-index: 1; }
          .subcontract-milestone-badge { display: inline-flex; align-items: center; border: 1px solid #d8e0ed; border-radius: 6px; padding: 3px 9px; color: #667085; background: #fff; }
          .responsibility-panel { border: 0; border-radius: 0; background: transparent; padding: 0; }
          .responsibility-option { border: 1px solid #eef2f7; border-radius: 8px; padding: 12px 14px; background: #fbfcff; }
          .responsibility-option .form-check { margin-bottom: 0; }
          .responsibility-option .form-check-label { color: #344054; font-size: 14px; line-height: 1.45; }
          .subcontract-ready-hero { background: #fff; border: 1px solid #dfe5ec; border-radius: 16px; padding: 34px 24px; margin-bottom: 22px; }
          .subcontract-ready-icon { width: 84px; height: 84px; border-radius: 999px; background: #ecfff4; color: #20bf6b; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 16px; }
          .signer-status-row { border: 1px solid #e2e8f0; border-radius: 12px; background: #fbfcff; padding: 16px; margin-bottom: 12px; }
          .signer-status-row.signed { background: #f3fff8; border-color: #a7e8c2; }
          .signer-status-row.pending { background: #fffaf0; border-color: #f2d18b; }
          .acknowledgment-check { display: flex; align-items: flex-start; gap: 12px; border: 1px solid #dfe5ec; border-radius: 10px; background: #fbfcff; padding: 14px 16px; color: #344054; line-height: 1.45; cursor: pointer; }
          .acknowledgment-check .form-check { min-height: auto; margin-bottom: 0; padding-left: 0; }
          .acknowledgment-check .form-check-input { margin-left: 0; cursor: pointer; }
          .acknowledgment-check span { font-size: 14px; }
          .ready-check-item { display: flex; gap: 12px; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid #edf1f5; }
          .ready-check-item:last-child { border-bottom: 0; }
          .ready-check-icon { width: 28px; height: 28px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
          .ready-check-icon.done { background: #e9fbf1; color: #14a35a; }
          .ready-check-icon.pending { background: #fff4dc; color: #c27803; }
          .generate-contract-modal .modal-content { border: 0; border-radius: 18px; box-shadow: 0 24px 70px rgba(15,23,42,.24); overflow: hidden; }
          .generate-contract-loader { padding: 30px 26px 26px; text-align: center; }
          .generate-contract-loader-icon { width: 64px; height: 64px; border-radius: 18px; background: #eef4ff; color: #2f57d6; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 18px; }
          .generate-contract-progress { height: 8px; border-radius: 999px; background: #eef2f7; overflow: hidden; margin-top: 22px; }
          .generate-contract-progress span { display: block; height: 100%; width: 42%; border-radius: inherit; background: linear-gradient(90deg, #2f57d6, #20c978); animation: generateContractProgress 1.2s ease-in-out infinite; }
          @keyframes generateContractProgress {
            0% { transform: translateX(-105%); }
            100% { transform: translateX(245%); }
          }
          @media (max-width: 767.98px) {
            .subcontract-wizard-card > .card-body { padding: 18px !important; }
            .selected-party-grid { grid-template-columns: 1fr; }
            .profile-search-row { grid-template-columns: 1fr; }
            .profile-result-row { grid-template-columns: 1fr; align-items: stretch; gap: 12px; }
            .profile-use-button { width: 100%; }
          }
        `}</style>

        {isNewHicFlow ? (
          <Alert
            variant={hicPrefillError ? "danger" : "info"}
            className="subcontract-context-banner d-flex align-items-start justify-content-between gap-3"
          >
            <div>
              <strong>
                {hicPrefillError
                  ? "Unable to connect homeowner project"
                  : "Connected to homeowner project"}
              </strong>
              <div className="small mt-1">
                {hicPrefillError ||
                  (hicPrefillLoading
                    ? "Loading homeowner and job-site details..."
                    : "GC, homeowner, and job-site details are inherited from the HIC and locked for this agreement.")}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() =>
                navigate(
                  `/create-contract?type=project&tab=Subcontractor&id=${hicProjectId}&action=1`,
                )
              }
            >
              Back to HIC
            </Button>
          </Alert>
        ) : null}

        <Modal
          show={saving === "send"}
          centered
          backdrop="static"
          keyboard={false}
          dialogClassName="generate-contract-modal"
          contentClassName="generate-contract-modal-content"
        >
          <Modal.Body className="p-0">
            <div className="generate-contract-loader">
              <span className="generate-contract-loader-icon">
                <Spinner animation="border" size="sm" />
              </span>
              <h5 className="mb-2">Generating Contract</h5>
              <p className="text-muted mb-0">
                Please wait while we prepare the PDF and signing links.
              </p>
              <div className="generate-contract-progress" aria-hidden="true">
                <span />
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Card className="subcontract-wizard-card border-0 shadow-sm">
          <Card.Body className="p-4 p-lg-5">
            {step > minimumStep && !generatedAgreement ? (
              <Button
                variant="link"
                className="px-0 mb-2 text-decoration-none"
                onClick={back}
              >
                <ArrowLeft size={16} className="me-1" /> Back
              </Button>
            ) : null}
            {!generatedAgreement ? (
              <StepProgress
                step={step}
                goToStep={goToStep}
                minimumStep={minimumStep}
                creatorRole={form.creatorRole}
              />
            ) : null}

            {step === 1 ? (
              <>
                <div className="text-center mb-4">
                  <h3 className="mb-2">Who are you in this contract?</h3>
                  <p className="text-muted mb-0">
                    This helps us set up the contract the right way.
                  </p>
                </div>
                <div className="d-grid gap-3">
                  <button
                    type="button"
                    className={`subcontract-choice rounded-3 p-4 text-start ${form.creatorRole === "contractor" ? "active" : ""}`}
                    onClick={() => selectCreatorRole("contractor")}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <div className="d-flex gap-3">
                        <span
                          className="rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center"
                          style={{ width: 52, height: 52 }}
                        >
                          <UsersRound />
                        </span>
                        <div>
                          <h5 className="mb-1">
                            I am Contractor hiring Subcontractor
                          </h5>
                          <p className="text-muted mb-0">
                            I am the contractor / GC creating a subcontract
                            agreement for a subcontractor.
                          </p>
                        </div>
                      </div>
                      <Form.Check
                        checked={form.creatorRole === "contractor"}
                        readOnly
                      />
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`subcontract-choice rounded-3 p-4 text-start ${form.creatorRole === "subcontractor" ? "active" : ""}`}
                    onClick={() => selectCreatorRole("subcontractor")}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <div className="d-flex gap-3">
                        <span
                          className="rounded-circle bg-success-subtle text-success d-inline-flex align-items-center justify-content-center"
                          style={{ width: 52, height: 52 }}
                        >
                          <HardHat />
                        </span>
                        <div>
                          <h5 className="mb-1">
                            I am Subcontractor working for Contractor / GC
                          </h5>
                          <p className="text-muted mb-0">
                            I am the subcontractor and I want to create a
                            contract with a contractor/GC.
                          </p>
                        </div>
                      </div>
                      <Form.Check
                        checked={form.creatorRole === "subcontractor"}
                        readOnly
                      />
                    </div>
                  </button>
                </div>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <div className="subcontract-section-card">
                  <div className="subcontract-section-header">
                    <span className="subcontract-section-icon">
                      <FileText size={24} />
                    </span>
                    <div>
                      <h4>Project Info</h4>
                      <p className="text-muted mb-0">
                        {hicProjectId
                          ? "Inherited from the homeowner contract. These project details are locked."
                          : "Add the project reference for this subcontract agreement."}
                      </p>
                    </div>
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <Field label="Project Name" required>
                        <Form.Control
                          required
                          disabled={Boolean(hicProjectId)}
                          isInvalid={
                            showStepValidation && !form.projectName.trim()
                          }
                          value={form.projectName}
                          onChange={(e) =>
                            update("projectName", e.target.value)
                          }
                          placeholder="Enter project name"
                        />
                        <Form.Control.Feedback type="invalid">
                          Project name is required.
                        </Form.Control.Feedback>
                      </Field>
                    </Col>
                    <Col md={6}>
                      <Field label="ZIP Code" required>
                        <div className="position-relative">
                          <Form.Control
                            required
                            disabled={Boolean(hicProjectId)}
                            maxLength={5}
                            inputMode="numeric"
                            isInvalid={Boolean(
                              (showStepValidation && !form.projectZip.trim()) ||
                                projectZipError,
                            )}
                            value={form.projectZip}
                            onChange={(e) => lookupProjectZip(e.target.value)}
                            placeholder="Enter ZIP"
                          />
                          {projectZipLoading ? (
                            <Spinner
                              size="sm"
                              animation="border"
                              className="position-absolute top-50 translate-middle-y"
                              style={{ right: 12 }}
                            />
                          ) : null}
                        </div>
                        <Form.Control.Feedback
                          type="invalid"
                          className={projectZipError ? "d-block" : ""}
                        >
                          {projectZipError || "ZIP code is required."}
                        </Form.Control.Feedback>
                      </Field>
                    </Col>
                    <Col xs={12}>
                      <Field label="Project Address" required>
                        <Form.Control
                          required
                          disabled={Boolean(hicProjectId)}
                          isInvalid={
                            showStepValidation && !form.projectSite.trim()
                          }
                          value={form.projectSite}
                          onChange={(e) =>
                            update("projectSite", e.target.value)
                          }
                          placeholder="Enter street address"
                        />
                        <Form.Control.Feedback type="invalid">
                          Project address is required.
                        </Form.Control.Feedback>
                      </Field>
                    </Col>
                    <Col md={6}>
                      <Field label="City" required>
                        <Form.Control
                          required
                          disabled
                          isInvalid={
                            showStepValidation && !form.projectCity.trim()
                          }
                          value={form.projectCity}
                          placeholder="Enter City"
                        />
                        <Form.Control.Feedback type="invalid">
                          City is required.
                        </Form.Control.Feedback>
                      </Field>
                    </Col>
                    <Col md={6}>
                      <Field label="State" required>
                        <Form.Control
                          required
                          disabled
                          isInvalid={
                            showStepValidation && !form.projectState.trim()
                          }
                          value={form.projectState}
                          placeholder="Enter State"
                        />
                        <Form.Control.Feedback type="invalid">
                          State is required.
                        </Form.Control.Feedback>
                      </Field>
                    </Col>
                    <Col md={6}>
                      <Field label="Country" required>
                        <Form.Control
                          required
                          disabled
                          isInvalid={
                            showStepValidation && !form.projectCountry.trim()
                          }
                          value={form.projectCountry}
                          placeholder="Enter Country"
                        />
                        <Form.Control.Feedback type="invalid">
                          Country is required.
                        </Form.Control.Feedback>
                      </Field>
                    </Col>
                  </Row>
                </div>

                <Row className="g-3">
                  <Col xs={12}>
                    <div className="subcontract-section-card">
                      <div className="subcontract-section-header">
                        <span className="subcontract-section-icon">
                          <FileSignature size={24} />
                        </span>
                        <div>
                          <h5>Scope of Work</h5>
                          <p className="text-muted mb-0">
                            Write the scope and attach the original file.
                          </p>
                        </div>
                      </div>
                      <Field label="Scope of Work (Optional)">
                        <div
                          className="rounded border bg-white overflow-auto"
                          style={{ maxHeight: 420 }}
                        >
                          <RichEditorTipTab
                            value={form.scopeOfWork}
                            onChange={(value: any) =>
                              update("scopeOfWork", value)
                            }
                          />
                        </div>
                      </Field>
                      <div className="mt-3">
                        <div className="fw-semibold mb-1">
                          Attach Scope of Work
                        </div>
                        <p className="small text-muted mb-2">
                          Upload a supporting PDF, just like the homeowner
                          contract.
                        </p>
                        <Dropzone
                          onDrop={(files: File[]) =>
                            files[0] && uploadScopeDocument(files[0])
                          }
                          accept={{ "application/pdf": [".pdf"] }}
                          maxFiles={1}
                          disabled={scopeUploading}
                        >
                          {({ getRootProps, getInputProps }: any) => (
                            <div
                              {...getRootProps()}
                              className="rounded border border-dashed bg-light text-center p-4"
                              style={{
                                cursor: scopeUploading ? "wait" : "pointer",
                              }}
                            >
                              <input {...getInputProps()} />
                              {scopeUploading ? (
                                <Spinner size="sm" className="me-2" />
                              ) : (
                                <UploadCloud
                                  size={28}
                                  className="text-primary mb-2"
                                />
                              )}
                              <div className="fw-semibold">
                                {scopeDocumentUrl
                                  ? "Change Scope Document"
                                  : "Upload Scope Document"}
                              </div>
                              <div className="small text-muted">PDF only</div>
                            </div>
                          )}
                        </Dropzone>
                        {scopeDocumentUrl ? (
                          <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <a
                                href={scopeDocumentUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View attached Scope of Work PDF
                              </a>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => setScopeDocumentUrl("")}
                              >
                                Remove
                              </Button>
                            </div>
                            <iframe
                              src={scopeDocumentUrl}
                              title="Scope of Work PDF"
                              className="w-100 rounded border"
                              style={{ height: 280 }}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {form.creatorRole === "contractor" ? (
                      <div className="subcontract-section-card">
                        <div className="subcontract-section-header">
                          <span className="subcontract-section-icon">
                            <ClipboardCheck size={24} />
                          </span>
                          <div>
                            <h5>Subcontractor Responsibilities</h5>
                            <p className="text-muted mb-0">
                              Choose the same responsibility options used in the
                              homeowner subcontract PDF.
                            </p>
                          </div>
                        </div>
                        <div className="responsibility-panel">
                          <Row className="g-2">
                            {[
                              [
                                "labor",
                                "Labor. Including, but not limited to, employees, subcontractors and any other individuals or agents.",
                              ],
                              [
                                "materials",
                                "Materials. Including, but not limited to, all supplies and products.",
                              ],
                              [
                                "equipment",
                                "Equipment. Including, but not limited to, machinery, accessories, or devices.",
                              ],
                              [
                                "travel",
                                "Travel. Including, but not limited to, ensuring that the above-mentioned responsibilities are provided at the project location.",
                              ],
                              ["other", "Other"],
                            ].map(([id, label]) => (
                              <Col lg={6} key={id}>
                                <div className="responsibility-option h-100">
                                  <Form.Check
                                    type="checkbox"
                                    id={`legacy-responsibility-${id}`}
                                    label={label}
                                    checked={form.legacyResponsibilities.includes(
                                      id,
                                    )}
                                    onChange={(e) =>
                                      updateLegacyResponsibility(
                                        id,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </div>
                              </Col>
                            ))}
                          </Row>
                          {form.legacyResponsibilities.includes("other") ? (
                            <Field
                              label="Description of Other Responsibilities"
                              required
                            >
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={form.descriptionOfOtherResponsibilities}
                                onChange={(e) =>
                                  update(
                                    "descriptionOfOtherResponsibilities",
                                    e.target.value,
                                  )
                                }
                                placeholder="Describe other subcontractor responsibilities..."
                              />
                            </Field>
                          ) : null}
                          {!form.legacyResponsibilities.length ? (
                            <Alert
                              variant="warning"
                              className="small py-2 mt-3 mb-0"
                            >
                              Select at least one responsibility before review.
                            </Alert>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </Col>
                  <Col xs={12}>
                    <div className="subcontract-section-card">
                      <div className="subcontract-section-header">
                        <span className="subcontract-section-icon">
                          <FileText size={24} />
                        </span>
                        <div>
                          <h5>Schedule & Exclusions</h5>
                          <p className="text-muted mb-0">
                            Set the contract dates and any excluded work.
                          </p>
                        </div>
                      </div>
                      <Row className="g-3">
                        <Col xs={12}>
                          <Field label="Exclusions">
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={form.exclusions}
                              onChange={(e) =>
                                update("exclusions", e.target.value)
                              }
                              placeholder="Enter any exclusions..."
                            />
                          </Field>
                        </Col>
                        <Col md={4}>
                          <Field label="Prime Contract Date" required>
                            <Form.Control
                              required
                              type="date"
                              isInvalid={
                                showStepValidation && !form.primeContractDate
                              }
                              value={form.primeContractDate}
                              onChange={(e) =>
                                update("primeContractDate", e.target.value)
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              Prime contract date is required.
                            </Form.Control.Feedback>
                          </Field>
                        </Col>
                        <Col md={4}>
                          <Field label="Commencement Date" required>
                            <Form.Control
                              required
                              type="date"
                              isInvalid={
                                showStepValidation && !form.commencementDate
                              }
                              value={form.commencementDate}
                              onChange={(e) =>
                                update("commencementDate", e.target.value)
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              Commencement date is required.
                            </Form.Control.Feedback>
                          </Field>
                        </Col>
                        <Col md={4}>
                          <Field label="Completion Date" required>
                            <Form.Control
                              required
                              type="date"
                              min={form.commencementDate || undefined}
                              isInvalid={Boolean(
                                (showStepValidation && !form.completionDate) ||
                                  (form.completionDate &&
                                    form.commencementDate &&
                                    new Date(form.completionDate) <
                                      new Date(form.commencementDate)),
                              )}
                              value={form.completionDate}
                              onChange={(e) =>
                                update("completionDate", e.target.value)
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {!form.completionDate
                                ? "Completion date is required."
                                : "Completion date must be on or after commencement."}
                            </Form.Control.Feedback>
                          </Field>
                        </Col>
                      </Row>
                      {form.creatorRole === "contractor" ? (
                        <>
                          <Row className="g-3 mt-1">
                            <Col md={6}>
                              <Field label="Contractor Authorized Representative">
                                <Form.Control
                                  value={
                                    form.contractorAuthorizedRepresentative
                                  }
                                  onChange={(e) =>
                                    update(
                                      "contractorAuthorizedRepresentative",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="First and last name"
                                />
                              </Field>
                            </Col>
                            <Col md={6}>
                              <Field label="Subcontractor Authorized Representative">
                                <Form.Control
                                  value={
                                    form.subcontractorAuthorizedRepresentative
                                  }
                                  onChange={(e) =>
                                    update(
                                      "subcontractorAuthorizedRepresentative",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="First and last name"
                                />
                              </Field>
                            </Col>
                          </Row>
                          <Row className="g-3 mt-1">
                            <Col md={6}>
                              <Field label="Completion Date Option" required>
                                <Form.Select
                                  required
                                  isInvalid={
                                    showStepValidation &&
                                    !form.completionDateOption
                                  }
                                  value={form.completionDateOption}
                                  onChange={(e) =>
                                    update(
                                      "completionDateOption",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">
                                    Select completion date option
                                  </option>
                                  <option value="specificDate">
                                    By the specific date of
                                  </option>
                                  <option value="industryStandards">
                                    In accordance with industry standards
                                  </option>
                                  <option value="other">Other</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  Completion date option is required.
                                </Form.Control.Feedback>
                              </Field>
                            </Col>
                            {form.completionDateOption === "specificDate" ? (
                              <Col md={6}>
                                <Field
                                  label="Completion Date - Specific"
                                  required
                                >
                                  <Form.Control
                                    required
                                    type="date"
                                    isInvalid={
                                      showStepValidation &&
                                      !form.completionDateSpecific
                                    }
                                    value={form.completionDateSpecific}
                                    onChange={(e) =>
                                      update(
                                        "completionDateSpecific",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Specific completion date is required.
                                  </Form.Control.Feedback>
                                </Field>
                              </Col>
                            ) : null}
                            {form.completionDateOption === "other" ? (
                              <Col md={6}>
                                <Field label="Completion Date - Other" required>
                                  <Form.Control
                                    required
                                    type="date"
                                    isInvalid={
                                      showStepValidation &&
                                      !form.completionDateOther
                                    }
                                    value={form.completionDateOther}
                                    onChange={(e) =>
                                      update(
                                        "completionDateOther",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Other completion date is required.
                                  </Form.Control.Feedback>
                                </Field>
                              </Col>
                            ) : null}
                            <Col md={6}>
                              <Field
                                label="Substantial Completion Date Option"
                                required
                              >
                                <Form.Select
                                  required
                                  isInvalid={
                                    showStepValidation &&
                                    !form.substantialCompletionDateOption
                                  }
                                  value={form.substantialCompletionDateOption}
                                  onChange={(e) =>
                                    update(
                                      "substantialCompletionDateOption",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">
                                    Select substantial completion option
                                  </option>
                                  <option value="specificDate">
                                    By the specific date of
                                  </option>
                                  <option value="industryStandards">
                                    In accordance with industry standards
                                  </option>
                                  <option value="other">Other</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  Substantial completion option is required.
                                </Form.Control.Feedback>
                              </Field>
                            </Col>
                            {form.substantialCompletionDateOption ===
                            "specificDate" ? (
                              <Col md={6}>
                                <Field
                                  label="Substantial Completion Date - Specific"
                                  required
                                >
                                  <Form.Control
                                    required
                                    type="date"
                                    isInvalid={
                                      showStepValidation &&
                                      !form.substantialCompletionDateSpecific
                                    }
                                    value={
                                      form.substantialCompletionDateSpecific
                                    }
                                    onChange={(e) =>
                                      update(
                                        "substantialCompletionDateSpecific",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Specific substantial completion date is
                                    required.
                                  </Form.Control.Feedback>
                                </Field>
                              </Col>
                            ) : null}
                            {form.substantialCompletionDateOption ===
                            "other" ? (
                              <Col md={6}>
                                <Field
                                  label="Substantial Completion Date - Other"
                                  required
                                >
                                  <Form.Control
                                    required
                                    type="date"
                                    isInvalid={
                                      showStepValidation &&
                                      !form.substantialCompletionDateOther
                                    }
                                    value={form.substantialCompletionDateOther}
                                    onChange={(e) =>
                                      update(
                                        "substantialCompletionDateOther",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Other substantial completion date is
                                    required.
                                  </Form.Control.Feedback>
                                </Field>
                              </Col>
                            ) : null}
                          </Row>
                        </>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <h4>{otherDetailsTitle}</h4>
                <p className="text-muted">
                  Search and select the{" "}
                  {form.creatorRole === "contractor"
                    ? "subcontractor"
                    : "contractor / GC"}{" "}
                  from RELiBUILD, or add them as a guest signer.
                </p>
                {renderOtherDetails()}
              </>
            ) : null}

            {step === 4 ? (
              <>
                <div className="subcontract-section-card">
                  <div className="subcontract-section-header">
                    <span className="subcontract-section-icon">
                      <FileText size={24} />
                    </span>
                    <div>
                      <h5>Add Milestones</h5>
                      <p className="text-muted mb-0">
                        Enter the total subcontract amount before adding
                        milestone percentages.
                      </p>
                    </div>
                  </div>
                  <Row className="g-3 align-items-center">
                    <Col lg={12}>
                      <Field label="Total Subcontract Amount" required>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          onFocus={(e) => e.target.select()}
                          isInvalid={
                            showStepValidation &&
                            !isValidAmount(form.contractSum)
                          }
                          value={form.contractSum}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!/^[0-9.,]*$/.test(value)) return;
                            if ((value.match(/\./g) || []).length > 1) return;
                            const dotIndex = value.indexOf(".");
                            if (
                              dotIndex !== -1 &&
                              value.slice(dotIndex + 1).length > 2
                            )
                              return;
                            if (value === "0" || value === "0.0") {
                              update("contractSum", "");
                              setMilestones((current) =>
                                current.map((item) => ({
                                  ...item,
                                  amount: "0.00",
                                })),
                              );
                              setMilestoneDraft((current) => ({
                                ...current,
                                amount: "0.00",
                              }));
                              return;
                            }
                            const contractSum = value;
                            update("contractSum", contractSum);
                            setMilestones((current) =>
                              current.map((item) => ({
                                ...item,
                                amount: calculateMilestoneAmount(
                                  item.percentage,
                                  contractSum,
                                ),
                              })),
                            );
                            setMilestoneDraft((current) => ({
                              ...current,
                              amount: calculateMilestoneAmount(
                                current.percentage,
                                contractSum,
                              ),
                            }));
                          }}
                          placeholder="Enter total subcontract amount"
                        />
                        <Form.Control.Feedback type="invalid">
                          Enter a valid numeric amount.
                        </Form.Control.Feedback>
                        <div className="small text-muted mt-2">
                          Milestone amounts are calculated from this total.
                        </div>
                      </Field>
                    </Col>
                  </Row>
                </div>

                {form.creatorRole === "contractor" ? (
                  <div className="subcontract-section-card">
                    <div className="subcontract-section-header">
                      <span className="subcontract-section-icon">
                        <FileText size={24} />
                      </span>
                      <div>
                        <h5>Legacy PDF Payment Terms</h5>
                        <p className="text-muted mb-0">
                          These options match the homeowner subcontractor PDF
                          sections VIII, IX, X, and assignment terms.
                        </p>
                      </div>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Field label="Payment Amount Option" required>
                          <Form.Select
                            required
                            isInvalid={
                              showStepValidation && !form.paymentAmountOption
                            }
                            value={form.paymentAmountOption}
                            onChange={(e) =>
                              update("paymentAmountOption", e.target.value)
                            }
                          >
                            <option value="">
                              Select payment amount option
                            </option>
                            <option value="totalAmount">Total Amount</option>
                            <option value="hourlyRate">Hourly Rate</option>
                            <option value="other">Other</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Payment amount option is required.
                          </Form.Control.Feedback>
                        </Field>
                      </Col>
                      {form.paymentAmountOption === "hourlyRate" ? (
                        <Col md={6}>
                          <Field label="Payment Amount - Hourly Rate" required>
                            <Form.Control
                              required
                              isInvalid={
                                showStepValidation &&
                                !form.paymentAmountHourlyRate.trim()
                              }
                              value={form.paymentAmountHourlyRate}
                              onChange={(e) =>
                                update(
                                  "paymentAmountHourlyRate",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter hourly rate"
                            />
                            <Form.Control.Feedback type="invalid">
                              Hourly rate is required.
                            </Form.Control.Feedback>
                          </Field>
                        </Col>
                      ) : null}
                      {form.paymentAmountOption === "other" ? (
                        <Col md={6}>
                          <Field
                            label="Payment Amount - Other Description"
                            required
                          >
                            <Form.Control
                              required
                              isInvalid={
                                showStepValidation &&
                                !form.paymentAmountOtherDescription.trim()
                              }
                              value={form.paymentAmountOtherDescription}
                              onChange={(e) =>
                                update(
                                  "paymentAmountOtherDescription",
                                  e.target.value,
                                )
                              }
                              placeholder="Describe payment amount"
                            />
                            <Form.Control.Feedback type="invalid">
                              Payment description is required.
                            </Form.Control.Feedback>
                          </Field>
                        </Col>
                      ) : null}
                      <Col md={6}>
                        <Field label="Payment Method Option" required>
                          <Form.Select
                            required
                            isInvalid={
                              showStepValidation && !form.paymentMethodOption
                            }
                            value={form.paymentMethodOption}
                            onChange={(e) =>
                              update("paymentMethodOption", e.target.value)
                            }
                          >
                            <option value="">
                              Select payment method option
                            </option>
                            <option value="immediate">
                              Immediately upon completion
                            </option>
                            <option value="within7days">
                              Within 7 business days
                            </option>
                            <option value="regular">
                              Weekly/monthly/quarterly/other regular payment
                            </option>
                            <option value="other">Other</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Payment method option is required.
                          </Form.Control.Feedback>
                        </Field>
                      </Col>
                      {form.paymentMethodOption === "other" ? (
                        <Col md={6}>
                          <Field label="Payment Method Option - Other" required>
                            <Form.Control
                              required
                              isInvalid={
                                showStepValidation &&
                                !form.paymentMethodOther.trim()
                              }
                              value={form.paymentMethodOther}
                              onChange={(e) =>
                                update("paymentMethodOther", e.target.value)
                              }
                              placeholder="Describe payment method"
                            />
                            <Form.Control.Feedback type="invalid">
                              Payment method description is required.
                            </Form.Control.Feedback>
                          </Field>
                        </Col>
                      ) : null}
                      {form.paymentMethodOption === "regular" ? (
                        <>
                          <Col md={6}>
                            <Field label="Payment Frequency Option" required>
                              <Form.Select
                                required
                                isInvalid={
                                  showStepValidation &&
                                  !form.paymentFrequencyOption
                                }
                                value={form.paymentFrequencyOption}
                                onChange={(e) =>
                                  update(
                                    "paymentFrequencyOption",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">
                                  Select payment frequency
                                </option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="other">Other</option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                Payment frequency is required.
                              </Form.Control.Feedback>
                            </Field>
                          </Col>
                          {form.paymentFrequencyOption === "other" ? (
                            <Col md={6}>
                              <Field
                                label="Payment Frequency Option - Other"
                                required
                              >
                                <Form.Control
                                  required
                                  isInvalid={
                                    showStepValidation &&
                                    !form.paymentFrequencyOther.trim()
                                  }
                                  value={form.paymentFrequencyOther}
                                  onChange={(e) =>
                                    update(
                                      "paymentFrequencyOther",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Describe payment frequency"
                                />
                                <Form.Control.Feedback type="invalid">
                                  Payment frequency description is required.
                                </Form.Control.Feedback>
                              </Field>
                            </Col>
                          ) : null}
                        </>
                      ) : null}
                      <Col md={6}>
                        <Field label="Right to Subcontracting Option" required>
                          <Form.Select
                            required
                            isInvalid={
                              showStepValidation && !form.rightToSubcontracting
                            }
                            value={form.rightToSubcontracting}
                            onChange={(e) =>
                              update("rightToSubcontracting", e.target.value)
                            }
                          >
                            <option value="">
                              Select subcontracting right
                            </option>
                            <option value="Right to Subcontracting">
                              Right to Subcontracting
                            </option>
                            <option value="No Right to Subcontracting">
                              No Right to Subcontracting
                            </option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Subcontracting option is required.
                          </Form.Control.Feedback>
                        </Field>
                      </Col>
                      <Col md={6}>
                        <Field label="Assignment Right Option" required>
                          <Form.Select
                            required
                            isInvalid={
                              showStepValidation && !form.assignmentRight
                            }
                            value={form.assignmentRight}
                            onChange={(e) =>
                              update("assignmentRight", e.target.value)
                            }
                          >
                            <option value="">Select assignment right</option>
                            <option value="subcontractorCanAssign">
                              Subcontractor can assign rights
                            </option>
                            <option value="subcontractorCannotAssign">
                              Subcontractor cannot assign rights
                            </option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Assignment right option is required.
                          </Form.Control.Feedback>
                        </Field>
                      </Col>
                    </Row>
                  </div>
                ) : null}

                <div className="subcontract-section-card">
                  <div className="subcontract-section-header">
                    <span className="subcontract-section-icon">
                      <HardHat size={24} />
                    </span>
                    <div>
                      <h5>Project Timeline</h5>
                      <p className="text-muted mb-0">
                        Follow your project's progress through each milestone.
                      </p>
                    </div>
                  </div>

                  <div
                    ref={milestoneWarningRef}
                    tabIndex={-1}
                    className={`milestone-percentage-summary mb-4 ${milestonePercentageTotal === 100 ? "complete" : milestonePercentageTotal > 100 ? "over" : "incomplete"}`}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                      <strong>Milestone Completion</strong>
                      <strong>{milestonePercentageTotal}% / 100%</strong>
                    </div>
                    <div
                      className="progress"
                      role="progressbar"
                      aria-label="Milestone percentage total"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.min(milestonePercentageTotal, 100)}
                    >
                      <div
                        className="progress-bar"
                        style={{
                          width: `${Math.min(milestonePercentageTotal, 100)}%`,
                        }}
                      />
                    </div>
                    <small className="d-block mt-2">
                      {milestonePercentageTotal === 100
                        ? "Complete. You can continue to Review."
                        : milestonePercentageTotal > 100
                          ? `Reduce milestones by ${milestonePercentageTotal - 100}%. Total must equal exactly 100%.`
                          : `${100 - milestonePercentageTotal}% remaining. Total must equal exactly 100%.`}
                    </small>
                    {milestonePercentageTotal === 100 ? (
                      <div className="milestone-complete-inline">
                        <span>
                          <CheckCircle2 size={14} />
                        </span>
                        Ready for next step
                      </div>
                    ) : (
                      <Alert variant="warning" className="py-2 px-3 mt-3 mb-0">
                        <strong>Action required:</strong> Milestone percentages
                        must total 100% before you can continue.
                      </Alert>
                    )}
                  </div>

                  {milestoneEditorOpen ? (
                    <div className="subcontract-milestone-editor mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h6 className="text-primary mb-0">
                          {editingMilestoneIndex === null
                            ? "Add New Milestone"
                            : "Update milestone"}
                        </h6>
                        <Button
                          variant="light"
                          className="btn-icon"
                          onClick={closeMilestoneEditor}
                          aria-label="Close milestone editor"
                        >
                          ×
                        </Button>
                      </div>
                      <Row className="g-3">
                        <Col xs={12}>
                          <Field label="Milestone Name" required>
                            <Form.Control
                              value={milestoneDraft.title}
                              onChange={(e) =>
                                updateMilestoneDraft("title", e.target.value)
                              }
                              placeholder="e.g., Foundation Complete"
                            />
                          </Field>
                        </Col>
                        <Col xs={12}>
                          <Field label="Percentage of Total Amount" required>
                            <div className="input-group">
                              <Form.Control
                                type="number"
                                min={0}
                                max={100}
                                step="0.1"
                                value={milestoneDraft.percentage}
                                onChange={(e) =>
                                  updateMilestoneDraft(
                                    "percentage",
                                    e.target.value,
                                  )
                                }
                                placeholder="0"
                              />
                              <span className="input-group-text">%</span>
                            </div>
                            {milestoneDraft.percentage ? (
                              <div className="small text-primary mt-1">
                                Amount: {normalizeMoney(milestoneDraft.amount)}
                              </div>
                            ) : null}
                          </Field>
                        </Col>
                        <Col xs={12}>
                          <Field label="Description (Optional)">
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={milestoneDraft.description}
                              onChange={(e) =>
                                updateMilestoneDraft(
                                  "description",
                                  e.target.value,
                                )
                              }
                              placeholder="Describe the work included in this milestone..."
                            />
                          </Field>
                        </Col>
                        <Col xs={12} className="d-flex gap-2">
                          <Button
                            className="flex-grow-1"
                            onClick={saveMilestone}
                          >
                            <CheckCircle2 size={16} className="me-2" />{" "}
                            {editingMilestoneIndex === null ? "Add" : "Update"}{" "}
                            Milestone
                          </Button>
                          <Button
                            variant="light"
                            onClick={closeMilestoneEditor}
                          >
                            Cancel
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  ) : null}

                  {milestones.length ? (
                    <div className="subcontract-timeline">
                      {milestones.map((item, index) => (
                        <div
                          className="subcontract-timeline-item"
                          key={`${item.title}-${index}`}
                        >
                          <span className="subcontract-timeline-dot" />
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="min-w-0">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <span className="small text-muted">
                                  Milestone {index + 1}
                                </span>
                                <ArrowRight size={14} className="text-muted" />
                                <strong>{item.title}</strong>
                              </div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="subcontract-milestone-badge">
                                  {item.percentage}%
                                </span>
                                <strong>{normalizeMoney(item.amount)}</strong>
                                <Badge bg="secondary">not started</Badge>
                              </div>
                              {item.description ? (
                                <p className="small text-muted mt-2 mb-0">
                                  {item.description}
                                </p>
                              ) : null}
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="light"
                                className="btn-icon text-primary"
                                onClick={() => editMilestone(index)}
                                aria-label={`Edit ${item.title}`}
                              >
                                <Pencil size={16} />
                              </Button>
                              <Button
                                variant="light"
                                className="btn-icon text-danger"
                                onClick={() => removeMilestone(index)}
                                aria-label={`Delete ${item.title}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed rounded text-center text-muted p-5">
                      No milestones yet. Add the first milestone to build the
                      project timeline.
                    </div>
                  )}
                  {!milestoneEditorOpen && milestonePercentageTotal < 100 ? (
                    <Button className="w-100 mt-3" onClick={openNewMilestone}>
                      <PlusCircle size={16} className="me-2" /> Add New
                      Milestone
                    </Button>
                  ) : null}
                </div>
                <div className="subcontract-section-card">
                  <div className="subcontract-section-header">
                    <span className="subcontract-section-icon">
                      <ShieldCheck size={24} />
                    </span>
                    <div>
                      <h5>Insurance Coverages</h5>
                      <p className="text-muted mb-0">
                        Select the insurance options that should appear in
                        Section 11.
                      </p>
                    </div>
                  </div>
                  <Row className="g-3">
                    <Col xs={12}>
                      <p className="text-muted small mb-3">
                        Unless different limits are stated in Schedule B, select
                        the options that should appear in Section 11.
                      </p>
                      <div className="insurance-options-list">
                        <Form.Check
                          type="radio"
                          name="commercial-general-liability"
                          id="insurance-cgl-carries"
                          label="Carries commercial general liability insurance as written with an insurance company authorized to issue policies in this State."
                          checked={
                            form.insuranceCommercialGeneralLiability === true &&
                            !form.insuranceSelfInsured
                          }
                          onChange={() => {
                            updateCheckbox(
                              "insuranceCommercialGeneralLiability",
                              true,
                            );
                            updateCheckbox("insuranceSelfInsured", false);
                            updateCheckbox(
                              "insuranceWorkersCompensation",
                              false,
                            );
                          }}
                        />
                        {form.insuranceCommercialGeneralLiability === true &&
                        !form.insuranceSelfInsured ? (
                          <div className="ms-4 mt-1 mb-2">
                            <Form.Control
                              value={form.insuranceCompanyName}
                              onChange={(e) =>
                                update("insuranceCompanyName", e.target.value)
                              }
                              placeholder="Enter insurance company name"
                            />
                          </div>
                        ) : null}
                        <Form.Check
                          type="radio"
                          name="commercial-general-liability"
                          id="insurance-cgl-does-not-carry"
                          label="The Contractor does not carry commercial general liability insurance."
                          checked={
                            form.insuranceCommercialGeneralLiability ===
                              false && !form.insuranceSelfInsured
                          }
                          onChange={() => {
                            updateCheckbox(
                              "insuranceCommercialGeneralLiability",
                              false,
                            );
                            updateCheckbox("insuranceSelfInsured", false);
                            updateCheckbox(
                              "insuranceWorkersCompensation",
                              false,
                            );
                            update("insuranceCompanyName", "");
                          }}
                        />
                        <Form.Check
                          type="radio"
                          name="commercial-general-liability"
                          id="insurance-self-insured"
                          label="The Contractor is self-insured."
                          checked={form.insuranceSelfInsured}
                          onChange={() => {
                            updateCheckbox("insuranceSelfInsured", true);
                            updateCheckbox(
                              "insuranceCommercialGeneralLiability",
                              null,
                            );
                            updateCheckbox(
                              "insuranceWorkersCompensation",
                              false,
                            );
                            update("insuranceCompanyName", "");
                          }}
                        />
                        <Form.Check
                          type="radio"
                          name="commercial-general-liability"
                          id="insurance-workers"
                          label="The Contractor carries Workers Compensation Insurance."
                          checked={form.insuranceWorkersCompensation}
                          onChange={() => {
                            updateCheckbox(
                              "insuranceWorkersCompensation",
                              true,
                            );
                            updateCheckbox("insuranceSelfInsured", false);
                            updateCheckbox(
                              "insuranceCommercialGeneralLiability",
                              null,
                            );
                            update("insuranceCompanyName", "");
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={12}>
                      <Field label="Insurance / Compliance Notes (Optional)">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={form.insuranceRequirements}
                          onChange={(e) =>
                            update("insuranceRequirements", e.target.value)
                          }
                          placeholder="Enter insurance limits, Schedule B requirements, certificates, endorsements, or compliance notes."
                        />
                      </Field>
                    </Col>
                    {form.creatorRole === "contractor" ? (
                      <>
                        {form.insuranceWorkersCompensation ? (
                          <Col md={6}>
                            <Field label="Contractor Workers Compensation Insurance Limit">
                              <Form.Control
                                value={form.workersCompensationInsuranceLimit}
                                onChange={(e) =>
                                  update(
                                    "workersCompensationInsuranceLimit",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter limit"
                              />
                            </Field>
                          </Col>
                        ) : null}
                        <Col xs={12}>
                          <hr />
                        </Col>
                        <Col md={6}>
                          <Field label="Subcontractor's Insurance" required>
                            <Form.Select
                              value={form.subcontractorInsurance}
                              onChange={(e) =>
                                update("subcontractorInsurance", e.target.value)
                              }
                            >
                              <option value="yes">
                                Carries commercial general liability insurance
                              </option>
                              <option value="no">
                                Does not carry commercial general liability
                                insurance
                              </option>
                              <option value="selfInsured">
                                Is self-insured
                              </option>
                            </Form.Select>
                          </Field>
                        </Col>
                        {form.subcontractorInsurance === "yes" ? (
                          <Col md={6}>
                            <Field label="Subcontractor Insurance Provider Name">
                              <Form.Control
                                value={form.subcontractorInsuranceProviderName}
                                onChange={(e) =>
                                  update(
                                    "subcontractorInsuranceProviderName",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter provider name"
                              />
                            </Field>
                          </Col>
                        ) : null}
                        <Col md={6}>
                          <Field
                            label="Subcontractor Workers Compensation"
                            required
                          >
                            <Form.Select
                              value={
                                form.subcontractorWorkersCompensationInsurance
                              }
                              onChange={(e) =>
                                update(
                                  "subcontractorWorkersCompensationInsurance",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="noEmployees">
                                No employees / exempt
                              </option>
                              <option value="hasInsurance">
                                Carries Workers Compensation Insurance
                              </option>
                            </Form.Select>
                          </Field>
                        </Col>
                        {form.subcontractorWorkersCompensationInsurance ===
                        "hasInsurance" ? (
                          <Col md={6}>
                            <Field label="Subcontractor Workers Compensation Insurance Limit">
                              <Form.Control
                                value={
                                  form.subcontractorWorkersCompensationInsuranceLimit
                                }
                                onChange={(e) =>
                                  update(
                                    "subcontractorWorkersCompensationInsuranceLimit",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter limit"
                              />
                            </Field>
                          </Col>
                        ) : null}
                      </>
                    ) : null}
                  </Row>
                </div>
              </>
            ) : null}

            {step === 5 && !generatedAgreement ? (
              <>
                <h4>
                  {isChangeOrder
                    ? "Review & Generate Change Order"
                    : "Review & Generate Contract"}
                </h4>
                <p className="text-muted">
                  Check each item before generating the{" "}
                  {isChangeOrder
                    ? "subcontract change order"
                    : "subcontract agreement"}
                  .
                </p>
                <div className="d-grid gap-3">
                  {reviewChecklist.map((item) => (
                    <Card
                      className={`border ${item.complete ? "border-success-subtle" : "border-warning-subtle"}`}
                      key={item.title}
                    >
                      <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
                        <div className="d-flex align-items-start gap-3">
                          <span
                            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${item.complete ? "bg-success text-white" : "bg-warning-subtle text-warning"}`}
                            style={{ width: 30, height: 30, flex: "0 0 auto" }}
                          >
                            {item.complete ? <CheckCircle2 size={18} /> : "!"}
                          </span>
                          <div>
                            <div className="fw-semibold">{item.title}</div>
                            <div className="small text-muted">
                              {item.detail}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg={item.complete ? "success" : "warning"}>
                            {item.complete ? "Complete" : "Needs review"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setStep(item.step)}
                          >
                            Edit
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
                <div className="subcontract-section-card mt-4">
                  <div className="subcontract-section-header">
                    <span className="subcontract-section-icon">
                      <ClipboardCheck size={24} />
                    </span>
                    <div>
                      <h5>Required Acknowledgments</h5>
                      <p className="text-muted mb-0">
                        Confirm the numbers before generating the contract.
                      </p>
                    </div>
                  </div>
                  <div className="d-grid gap-3">
                    <label className="acknowledgment-check">
                      <Form.Check
                        type="checkbox"
                        id="figures-liability-acknowledgment"
                        checked={figuresAcknowledged}
                        onChange={(event) =>
                          setFiguresAcknowledged(event.currentTarget.checked)
                        }
                        className="mt-1"
                      />
                      <span>
                        <strong>I AGREE</strong> - I have reviewed all figures,
                        pricing, costs, fees, Contract Sum, and financial terms,
                        and I accept full responsibility for their accuracy
                        before this contract is generated.
                      </span>
                    </label>
                    {aiExtractedContractSum ? (
                      <label className="acknowledgment-check">
                        <Form.Check
                          type="checkbox"
                          id="ai-contract-sum-verification"
                          checked={aiAmountVerified}
                          onChange={(event) =>
                            setAiAmountVerified(event.currentTarget.checked)
                          }
                          className="mt-1"
                        />
                        <span>
                          <strong>I VERIFY</strong> - AI found a total amount in
                          the uploaded scope/invoice and filled Contract Sum as{" "}
                          <strong>{normalizeMoney(form.contractSum)}</strong>. I
                          understand AI can be incorrect, and I have manually
                          checked this amount against the uploaded document.
                        </span>
                      </label>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}

            {generatedAgreement
              ? (() => {
                  const contractorSigned = Boolean(
                    generatedAgreement.contractorSignatureUrl,
                  );
                  const subcontractorSigned = Boolean(
                    generatedAgreement.subcontractorSignatureUrl,
                  );
                  const contractorGuest =
                    !generatedAgreement.contractorUserId &&
                    !form.contractorUserId;
                  const subcontractorGuest =
                    !generatedAgreement.subcontractorUserId &&
                    !form.subcontractorUserId;
                  const generatedAt = generatedAgreement.sentAt
                    ? new Date(generatedAgreement.sentAt).toLocaleString()
                    : "Generated now";
                  const renderSignerRow = (
                    roleLabel: string,
                    role: "contractor" | "subcontractor",
                    email: string,
                    signed: boolean,
                    signedAt?: string,
                    isGuest?: boolean,
                  ) => (
                    <div
                      className={`signer-status-row ${signed ? "signed" : "pending"}`}
                    >
                      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                        <div>
                          <div className="fw-semibold">{roleLabel}</div>
                          <div className="text-muted small">
                            {email || "Email pending"}
                          </div>
                          <div className="text-muted small mt-1">
                            {signed
                              ? `Signed${signedAt ? ` on ${new Date(signedAt).toLocaleDateString()}` : ""}`
                              : isGuest
                                ? "Signing email sent with a secure guest link."
                                : "Signing email sent. Registered signer can also sign from their account."}
                          </div>
                        </div>
                        <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                          <Badge bg={signed ? "success" : "warning"}>
                            {signed ? "Signed" : "Pending"}
                          </Badge>
                          {!signed &&
                          generatedAgreement.status !== "fully_executed" ? (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={resendingGuestInvite}
                              onClick={() => resendSignatureEmail(role)}
                            >
                              {resendingGuestInvite ? (
                                <Spinner size="sm" className="me-1" />
                              ) : (
                                <RefreshCw size={14} className="me-1" />
                              )}
                              Resend Email
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <div>
                      <div className="subcontract-ready-hero text-center">
                        <span className="subcontract-ready-icon">
                          <CheckCircle2 size={46} />
                        </span>
                        <h3>
                          {generatedAgreement.status === "fully_executed"
                            ? "Contract Fully Executed"
                            : isChangeOrder
                              ? "Subcontract Change Order Ready"
                              : "Subcontract Agreement Ready"}
                        </h3>
                        <p className="text-muted mb-0">
                          {generatedAgreement.status === "fully_executed"
                            ? "Both parties have signed this subcontract agreement."
                            : "PDF is generated. Finish the signature flow from the actions below."}
                        </p>
                      </div>

                      <div className="subcontract-section-card">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <span className="subcontract-section-icon">
                              <FileText size={24} />
                            </span>
                            <div>
                              <div className="fw-semibold">
                                Subcontract_Agreement_
                                {form.contractorCompany || "Contract"}.pdf
                              </div>
                              <small className="text-muted">
                                Generated PDF · {generatedAt}
                              </small>
                            </div>
                          </div>
                          {generatedAgreement?.contractUrl ? (
                            <a
                              className="btn btn-outline-primary"
                              target="_blank"
                              rel="noreferrer"
                              href={generatedAgreement.contractUrl}
                            >
                              <Eye size={16} className="me-1" /> Preview PDF
                            </a>
                          ) : null}
                        </div>
                      </div>

                      <Row className="g-3">
                        <Col lg={7}>
                          <div className="subcontract-section-card h-100">
                            <div className="subcontract-section-header">
                              <span className="subcontract-section-icon">
                                <UsersRound size={24} />
                              </span>
                              <div>
                                <h5>Signing Status</h5>
                                <p className="text-muted mb-0">
                                  Track who has signed and who still needs the
                                  signing link.
                                </p>
                              </div>
                            </div>
                            {renderSignerRow(
                              "Contractor / GC",
                              "contractor",
                              form.contractorEmail,
                              contractorSigned,
                              generatedAgreement.contractorSignedAt,
                              contractorGuest,
                            )}
                            {renderSignerRow(
                              "Subcontractor",
                              "subcontractor",
                              form.subcontractorEmail,
                              subcontractorSigned,
                              generatedAgreement.subcontractorSignedAt,
                              subcontractorGuest,
                            )}
                          </div>
                        </Col>
                        <Col lg={5}>
                          <div className="subcontract-section-card h-100">
                            <div className="subcontract-section-header">
                              <span className="subcontract-section-icon">
                                <Clock3 size={24} />
                              </span>
                              <div>
                                <h5>Next Steps</h5>
                                <p className="text-muted mb-0">
                                  Use this checklist to complete the agreement
                                  cleanly.
                                </p>
                              </div>
                            </div>
                            <div className="ready-check-item">
                              <span className="ready-check-icon done">
                                <CheckCircle2 size={16} />
                              </span>
                              <div>
                                <div className="fw-semibold">PDF generated</div>
                                <div className="text-muted small">
                                  The latest agreement is ready for review.
                                </div>
                              </div>
                            </div>
                            <div className="ready-check-item">
                              <span
                                className={`ready-check-icon ${contractorSigned ? "done" : "pending"}`}
                              >
                                {contractorSigned ? (
                                  <CheckCircle2 size={16} />
                                ) : (
                                  <Clock3 size={15} />
                                )}
                              </span>
                              <div>
                                <div className="fw-semibold">
                                  Contractor / GC signature
                                </div>
                                <div className="text-muted small">
                                  {contractorSigned
                                    ? "Completed."
                                    : "Email sent, waiting for signature."}
                                </div>
                              </div>
                            </div>
                            <div className="ready-check-item">
                              <span
                                className={`ready-check-icon ${subcontractorSigned ? "done" : "pending"}`}
                              >
                                {subcontractorSigned ? (
                                  <CheckCircle2 size={16} />
                                ) : (
                                  <Clock3 size={15} />
                                )}
                              </span>
                              <div>
                                <div className="fw-semibold">
                                  Subcontractor signature
                                </div>
                                <div className="text-muted small">
                                  {subcontractorSigned
                                    ? "Completed."
                                    : "Email sent, waiting for signature."}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                })()
              : null}

            <div className="subcontract-action-bar d-flex flex-wrap justify-content-between gap-2 mt-4">
              <Button
                variant="outline-primary"
                disabled={!!saving}
                onClick={() => submit("draft")}
              >
                {saving === "draft" ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <FileText size={16} className="me-2" />
                )}
                Save & Exit
              </Button>
              {generatedAgreement ? (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={() =>
                      navigate(
                        canReturnToHic
                          ? `/create-contract?type=project&tab=Subcontractor&id=${hicProjectId}&action=1`
                          : "/get-contracts?view=subcontracts",
                      )
                    }
                  >
                    {canReturnToHic ? "Back to HIC" : "Back to Subcontracts"}
                  </Button>
                  {generatedAgreement.canSign ? (
                    <Button
                      onClick={() => openSignatureModal(generatedAgreement)}
                    >
                      <FileSignature size={16} className="me-1" /> Sign as{" "}
                      {generatedAgreement.viewerRole === "subcontractor"
                        ? "Subcontractor"
                        : "Contractor"}
                    </Button>
                  ) : null}
                </div>
              ) : step < 5 ? (
                <Button onClick={next}>
                  Continue <ArrowRight size={16} className="ms-1" />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={
                    !!saving ||
                    !figuresAcknowledged ||
                    Boolean(aiExtractedContractSum && !aiAmountVerified)
                  }
                  onClick={() => submit("send")}
                >
                  {saving === "send" ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <FileText size={16} className="me-2" />
                  )}
                  {isChangeOrder
                    ? "Generate Change Order"
                    : "Generate Contract"}
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      </Container>

      <ContractLoadingModal
        title={
          isChangeOrder
            ? "Subcontract Change Order Review and Signature"
            : "Subcontract Agreement Review and Signature"
        }
        open={showSignatureModal}
        pdfURL={(generatedAgreement || loadedAgreement)?.contractUrl}
        project={null}
        sigCanvasRef={signatureRef}
        submitSignature={submitAgreementSignature}
        submitting={signing}
        submitLabel="Submit Signed Contract"
        onClose={() => {
          if (!signing) setShowSignatureModal(false);
        }}
      />
      <SigningProgressModal status={signingStatus} />
      <ProcessingModal
        open={showScopeExtraction}
        currentStage={scopeExtractionStage}
        extractedHTML={extractedScopePreview}
      />
    </div>
  );
};

export default SubcontractAgreementPage;
